import React, { useState, useEffect, useCallback } from 'react';
import { 
  Phone, Calendar, FileText, Brain, Hash, Pill, Bell, DollarSign,
  Play, Pause, ChevronLeft, ChevronRight, Check, AlertCircle, Clock,
  User, Stethoscope, Activity, Shield, BarChart3, TrendingUp, BookOpen,
  FileCheck, Search, Database, Lightbulb, ExternalLink, PieChart,
  ArrowUpRight, ArrowDownRight, Minus, Scissors, Heart, Clipboard,
  UserCheck, ClipboardList, Syringe, Thermometer, BedDouble, Home,
  LineChart, AlertTriangle, CheckCircle2, Timer,
  Eye, Scan, Layers, Target, Navigation2, FlaskConical, Users
} from 'lucide-react';

// Scenario data for different clinical contexts
const SCENARIOS = {
  gp: {
    id: 'gp',
    name: 'GP Clinic',
    patientName: 'James Wilson',
    age: 45,
    symptoms: 'Fever, sore throat, chest tightness for 3 days',
    diagnosis: 'Acute pharyngitis with possible lower respiratory involvement',
    icdCodes: ['J02.9 Acute pharyngitis', 'R50.9 Fever, unspecified', 'R06.0 Dyspnea'],
    labs: ['CBC', 'CRP', 'Throat swab culture'],
    medications: ['Amoxicillin 500mg TDS', 'Paracetamol 500mg PRN'],
    followUp: '5 days',
    guidelines: ['NICE CG191', 'PHE Antibiotic Guidelines'],
    redFlags: ['Stridor', 'Respiratory distress', 'Inability to swallow'],
    differentials: [
      { name: 'Acute pharyngitis', probability: 78 },
      { name: 'Viral URTI', probability: 65 },
      { name: 'Early pneumonia', probability: 25 }
    ],
    analytics: {
      consultToday: 47,
      avgDuration: '8.2 min',
      docCompleteness: 94,
      guidelineAdherence: 92
    },
    isSurgical: false
  },
  hospital: {
    id: 'hospital',
    name: 'Hospital Outpatient',
    patientName: 'Maria Garcia',
    age: 62,
    symptoms: 'Progressive shortness of breath, orthopnea, ankle swelling for 2 weeks',
    diagnosis: 'Decompensated heart failure, NYHA Class III',
    icdCodes: ['I50.9 Heart failure', 'R06.0 Dyspnea', 'R60.0 Localized edema'],
    labs: ['BNP', 'Renal function panel', 'Chest X-ray', 'ECG'],
    medications: ['Furosemide 40mg BD', 'Ramipril 2.5mg OD', 'Bisoprolol 2.5mg OD'],
    followUp: '1 week',
    guidelines: ['ESC Heart Failure Guidelines 2023', 'NICE NG106'],
    redFlags: ['Acute pulmonary edema', 'Cardiogenic shock', 'Severe hypoxia'],
    differentials: [
      { name: 'Heart failure exacerbation', probability: 85 },
      { name: 'Pulmonary embolism', probability: 20 },
      { name: 'COPD exacerbation', probability: 15 }
    ],
    analytics: {
      consultToday: 112,
      avgDuration: '12.5 min',
      docCompleteness: 91,
      guidelineAdherence: 88
    },
    isSurgical: false
  },
  diabetes: {
    id: 'diabetes',
    name: 'Diabetes Follow-up',
    patientName: 'Robert Chen',
    age: 58,
    symptoms: 'Routine diabetes review, occasional blurred vision, tingling in feet',
    diagnosis: 'Type 2 diabetes with early peripheral neuropathy',
    icdCodes: ['E11.9 Type 2 DM', 'G63.2 Diabetic polyneuropathy', 'H36.0 Diabetic retinopathy'],
    labs: ['HbA1c', 'Lipid panel', 'Renal function', 'Urine ACR'],
    medications: ['Metformin 1g BD', 'Gliclazide 80mg OD', 'Atorvastatin 20mg ON'],
    followUp: '3 months',
    guidelines: ['ADA Standards of Care 2024', 'NICE NG28'],
    redFlags: ['DKA symptoms', 'Hypoglycemia', 'Foot ulceration'],
    differentials: [
      { name: 'T2DM with neuropathy', probability: 92 },
      { name: 'B12 deficiency', probability: 35 },
      { name: 'Retinopathy progression', probability: 40 }
    ],
    analytics: {
      consultToday: 38,
      avgDuration: '15.3 min',
      docCompleteness: 96,
      guidelineAdherence: 94
    },
    isSurgical: false
  },
  ent: {
    id: 'ent',
    name: 'ENT Triage',
    patientName: 'Sophie Taylor',
    age: 28,
    symptoms: 'Recurrent ear pain, hearing loss, tinnitus for 6 weeks',
    diagnosis: 'Chronic otitis media with effusion',
    icdCodes: ['H65.2 Chronic serous otitis media', 'H91.9 Hearing loss', 'H93.1 Tinnitus'],
    labs: ['Audiometry', 'Tympanometry'],
    medications: ['Fluticasone nasal spray', 'Pseudoephedrine PRN'],
    followUp: '6 weeks',
    guidelines: ['NICE NG91', 'AAO-HNS Clinical Practice Guidelines'],
    redFlags: ['Facial weakness', 'Severe vertigo', 'Mastoid tenderness'],
    differentials: [
      { name: 'Otitis media with effusion', probability: 82 },
      { name: 'Eustachian tube dysfunction', probability: 70 },
      { name: 'Cholesteatoma', probability: 12 }
    ],
    analytics: {
      consultToday: 24,
      avgDuration: '10.8 min',
      docCompleteness: 93,
      guidelineAdherence: 90
    },
    isSurgical: false
  },
  surgical: {
    id: 'surgical',
    name: 'Surgical (ERAS)',
    patientName: 'David Thompson',
    age: 67,
    symptoms: 'Right lower quadrant pain, nausea, localized tenderness, elevated WCC',
    diagnosis: 'Acute appendicitis requiring laparoscopic appendectomy',
    procedure: 'Laparoscopic Appendectomy',
    icdCodes: ['K35.80 Acute appendicitis', 'K65.0 Generalized peritonitis', 'R10.31 RLQ pain'],
    labs: ['CBC', 'CRP', 'Renal panel', 'Coagulation screen', 'Group & Save'],
    medications: ['Paracetamol 1g QDS', 'Ondansetron 4mg PRN', 'LMWH prophylaxis'],
    followUp: '2 weeks',
    guidelines: ['ERAS Society Guidelines', 'NICE Appendicitis Pathway', 'ASA Guidelines'],
    redFlags: ['Sepsis', 'Perforation', 'DVT/PE', 'Wound infection'],
    differentials: [
      { name: 'Acute appendicitis', probability: 92 },
      { name: 'Mesenteric adenitis', probability: 15 },
      { name: 'Ovarian pathology', probability: 8 }
    ],
    riskScore: {
      asa: 'II',
      frailty: 'Low',
      surgical: 'Low-Medium',
      vte: 'Moderate'
    },
    erasMetrics: {
      losTarget: '1-2 days',
      mobilisationTarget: '< 6 hours',
      oralIntakeTarget: '< 4 hours',
      complicationRate: '< 5%'
    },
    analytics: {
      surgeriesToday: 12,
      avgLOS: '1.8 days',
      erasCompliance: 94,
      complicationRate: 3.2
    },
    isSurgical: true
  },
  retina: {
    id: 'retina',
    name: 'Retina AI (DR/DME)',
    patientName: 'Priya Sharma',
    age: 54,
    symptoms: 'Routine diabetic eye screening, blurred vision in right eye, known Type 2 DM for 12 years',
    diagnosis: 'Moderate NPDR with center-involving DME (CI-DME) in right eye',
    icdCodes: ['E11.321 Moderate NPDR with DME', 'H35.81 Retinal edema', 'E11.65 Type 2 DM with hyperglycemia'],
    labs: ['HbA1c: 8.2%', 'BP: 145/92 mmHg', 'eGFR: 68 mL/min', 'Urine ACR: 45 mg/g'],
    medications: ['Anti-VEGF intravitreal injection (Aflibercept 2mg)', 'Metformin 1g BD', 'Telmisartan 40mg OD'],
    followUp: '4 weeks post-injection',
    guidelines: ['ICO DR Guidelines 2024', 'AAO Preferred Practice Pattern', 'ETDRS Protocol'],
    redFlags: ['Sudden vision loss', 'Vitreous hemorrhage', 'Neovascularization at disc', 'Tractional detachment'],
    differentials: [
      { name: 'CI-DME (Center-involving)', probability: 88 },
      { name: 'Non-CI DME', probability: 45 },
      { name: 'Proliferative DR', probability: 22 }
    ],
    retinaBiomarkers: {
      intraretinalFluid: 'Present — 3 cysts detected',
      subretinalFluid: 'Minimal — 12 microns',
      crt: '385 microns (elevated)',
      drilScore: 'Moderate disorganization',
      hrf: '6 hyperreflective foci',
      fluidVolume: '0.42 mm³'
    },
    treatmentResponse: {
      velocity: 'Slow responder',
      visitsTrend: ['412', '398', '392', '385'],
      dryWeightTarget: '280 microns',
      nextAction: 'Consider switching anti-VEGF agent'
    },
    analytics: {
      patientsMonitored: 342,
      avgInjectionInterval: '5.2 weeks',
      treatmentCompliance: 78,
      visionStabilized: 89
    },
    isSurgical: false,
    isRetina: true
  },
  ivf: {
    id: 'ivf',
    name: 'IVF Centre',
    patientName: 'Mrs. Pallavi & Mr. Karthik',
    age: 32,
    symptoms: 'Primary infertility, 4 years duration, previous 2 failed IUI cycles, irregular cycles (PCOS)',
    diagnosis: 'Primary Infertility — PCOS with anovulation, mild male factor',
    icdCodes: ['N97.0 Female infertility — anovulation', 'E28.2 PCOS', 'N46.0 Male factor infertility'],
    labs: ['AMH: 4.8 ng/mL', 'FSH: 5.84 mIU/mL', 'LH: 8.2 mIU/mL', 'TSH: 2.99 µIU/mL', 'HbA1c: 5.4%'],
    medications: ['Gonal-F 150 IU daily', 'Cetrotide 0.25mg (from Day 6)', 'Trigger: hCG 10,000 IU'],
    followUp: 'OPU Day + 5 for embryo transfer',
    guidelines: ['ESHRE IVF Guidelines 2024', 'ICMR ART Guidelines', 'ASRM Practice Committee'],
    redFlags: ['OHSS risk (AMH >3.5)', 'Premature LH surge', 'Poor response', 'Failed fertilization'],
    differentials: [
      { name: 'PCOS with anovulation', probability: 88 },
      { name: 'Unexplained infertility', probability: 30 },
      { name: 'Tubal factor', probability: 12 }
    ],
    ivfData: {
      coupleProfile: {
        female: { age: 32, bmi: 24.2, amh: 4.8, afc: 18, lmp: 'CD 2', infertilityType: 'Primary', duration: '4 years' },
        male: { age: 35, semenCount: '32 million/mL', motility: '38%', morphology: '4%', dnaFrag: '18%' }
      },
      stimulation: {
        protocol: 'Antagonist',
        startDose: 'Gonal-F 150 IU',
        stimDays: 10,
        antagonistStart: 'Day 6'
      },
      monitoring: {
        follicles: { right: ['18mm', '16mm', '14mm', '12mm', '10mm'], left: ['17mm', '15mm', '13mm', '11mm'] },
        endometrium: '10.2mm Triple-line',
        e2Level: '2,450 pg/mL',
        triggerReady: true
      },
      embryology: {
        oocytesRetrieved: 12,
        mii: 10,
        fertilized: 8,
        day3Embryos: 7,
        day5Blastocysts: 4,
        gradeA: 2,
        gradeB: 2,
        frozen: 2,
        transferred: 2
      },
      outcome: {
        betaHCG: '286 mIU/mL',
        status: 'Positive',
        heartbeat: 'Confirmed at 6 weeks'
      }
    },
    analytics: {
      cyclesManaged: 186,
      avgOocytes: 11.4,
      fertRate: 76,
      clinicalPregRate: 52
    },
    isSurgical: false,
    isRetina: false,
    isIVF: true
  }
};

// Simulation steps - now includes Analytics
const STEPS = [
  { id: 1, title: 'Patient calls SkyDoc Voice Agent', icon: Phone },
  { id: 2, title: 'Appointment booked automatically', icon: Calendar },
  { id: 3, title: 'Consultation captured in real time', icon: FileText },
  { id: 4, title: 'Evidence-based clinical intelligence', icon: Brain },
  { id: 5, title: 'ICD-10 codes generated', icon: Hash },
  { id: 6, title: 'Labs and prescriptions prepared', icon: Pill },
  { id: 7, title: 'Follow-up and reminders triggered', icon: Bell },
  { id: 8, title: 'Revenue and billing intelligence', icon: DollarSign },
  { id: 9, title: 'Analytics & Intelligence Studio', icon: BarChart3 }
];

// Surgical ERAS Steps with phases
const SURGICAL_STEPS = [
  { id: 1, title: 'Surgical Triage & Referral', icon: Phone, phase: 'Preoperative' },
  { id: 2, title: 'Pre-op Assessment & Risk Scoring', icon: Clipboard, phase: 'Preoperative' },
  { id: 3, title: 'Prehabilitation & Optimization', icon: Heart, phase: 'Preoperative' },
  { id: 4, title: 'Patient Education & Consent', icon: UserCheck, phase: 'Preoperative' },
  { id: 5, title: 'Day of Surgery Coordination', icon: ClipboardList, phase: 'Perioperative' },
  { id: 6, title: 'Intraoperative Documentation', icon: Scissors, phase: 'Perioperative' },
  { id: 7, title: 'Postoperative Monitoring', icon: Thermometer, phase: 'Postoperative' },
  { id: 8, title: 'Early Mobilisation & Recovery', icon: BedDouble, phase: 'Postoperative' },
  { id: 9, title: 'Discharge Planning', icon: Home, phase: 'Discharge' },
  { id: 10, title: 'Post-discharge Follow-up', icon: Bell, phase: 'Discharge' },
  { id: 11, title: 'Outcomes & Revenue Intelligence', icon: LineChart, phase: 'Analytics' }
];

// Retina DR/DME Steps
const RETINA_STEPS = [
  { id: 1, title: 'Fundus & OCT Scan Capture', icon: Eye, phase: 'Imaging' },
  { id: 2, title: 'AI Retinal Segmentation', icon: Scan, phase: 'Imaging' },
  { id: 3, title: 'Biomarker Detection & Scoring', icon: Target, phase: 'Analysis' },
  { id: 4, title: 'Fluid Quantification (Voxel)', icon: Layers, phase: 'Analysis' },
  { id: 5, title: 'Longitudinal Comparison', icon: TrendingUp, phase: 'Analysis' },
  { id: 6, title: 'Risk Stratification', icon: AlertTriangle, phase: 'Intelligence' },
  { id: 7, title: 'Treatment Recommendation', icon: Brain, phase: 'Intelligence' },
  { id: 8, title: 'Patient Progress Report', icon: UserCheck, phase: 'Engagement' },
  { id: 9, title: 'Follow-up & Adherence', icon: Navigation2, phase: 'Engagement' },
  { id: 10, title: 'Outcomes & Analytics', icon: BarChart3, phase: 'Analytics' }
];

// IVF Centre Steps
const IVF_STEPS = [
  { id: 1, title: 'Couple Assessment & History', icon: Users, phase: 'Assessment' },
  { id: 2, title: 'Investigations & Labs', icon: FlaskConical, phase: 'Assessment' },
  { id: 3, title: 'Diagnosis & Treatment Decision', icon: Brain, phase: 'Planning' },
  { id: 4, title: 'Ovarian Stimulation Protocol', icon: Pill, phase: 'Stimulation' },
  { id: 5, title: 'Follicular Monitoring', icon: Scan, phase: 'Stimulation' },
  { id: 6, title: 'Trigger & Egg Retrieval (OPU)', icon: Target, phase: 'Procedure' },
  { id: 7, title: 'Embryology Lab (ICSI)', icon: Layers, phase: 'Procedure' },
  { id: 8, title: 'Embryo Transfer', icon: Heart, phase: 'Transfer' },
  { id: 9, title: 'Pregnancy Monitoring', icon: Activity, phase: 'Outcome' },
  { id: 10, title: 'IVF Analytics Dashboard', icon: BarChart3, phase: 'Outcome' }
];

const InteractiveLiveSimulation = ({ isDark }) => {
  const [activeStep, setActiveStep] = useState(1);
  const [activeScenario, setActiveScenario] = useState('gp');
  const [isPlaying, setIsPlaying] = useState(false);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [retinaUnlocked, setRetinaUnlocked] = useState(() => sessionStorage.getItem('skydoc-retina-access') === 'true');
  const [retinaPassword, setRetinaPassword] = useState('');
  const [retinaPasswordError, setRetinaPasswordError] = useState(false);
  const [showRetinaGate, setShowRetinaGate] = useState(false);
  const [ivfUnlocked, setIvfUnlocked] = useState(() => sessionStorage.getItem('skydoc-ivf-access') === 'true');
  const [ivfPassword, setIvfPassword] = useState('');
  const [ivfPasswordError, setIvfPasswordError] = useState(false);
  const [showIvfGate, setShowIvfGate] = useState(false);

  const RETINA_PASSWORD = 'SkydocRetina2026';
  const IVF_PASSWORD = 'Skydocivf2026';

  const handleRetinaAccess = (e) => {
    e.preventDefault();
    if (retinaPassword === RETINA_PASSWORD) {
      setRetinaUnlocked(true);
      sessionStorage.setItem('skydoc-retina-access', 'true');
      setShowRetinaGate(false);
      setRetinaPassword('');
      setRetinaPasswordError(false);
      handleScenarioChange('retina');
    } else {
      setRetinaPasswordError(true);
    }
  };

  const handleIvfAccess = (e) => {
    e.preventDefault();
    if (ivfPassword === IVF_PASSWORD) {
      setIvfUnlocked(true);
      sessionStorage.setItem('skydoc-ivf-access', 'true');
      setShowIvfGate(false);
      setIvfPassword('');
      setIvfPasswordError(false);
      handleScenarioChange('ivf');
    } else {
      setIvfPasswordError(true);
    }
  };

  const scenario = SCENARIOS[activeScenario];
  const isSurgical = scenario.isSurgical;
  const isRetina = scenario.isRetina || false;
  const isIVF = scenario.isIVF || false;
  const currentSteps = isIVF ? IVF_STEPS : isRetina ? RETINA_STEPS : isSurgical ? SURGICAL_STEPS : STEPS;
  const totalSteps = currentSteps.length;

  // Autoplay logic
  useEffect(() => {
    let interval;
    if (isPlaying) {
      interval = setInterval(() => {
        setActiveStep((prev) => {
          if (prev < totalSteps) {
            setCompletedSteps((completed) => 
              completed.includes(prev) ? completed : [...completed, prev]
            );
            return prev + 1;
          } else {
            setIsPlaying(false);
            setCompletedSteps(Array.from({length: totalSteps}, (_, i) => i + 1));
            return prev;
          }
        });
      }, 4000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, totalSteps]);

  const handleStepClick = useCallback((stepId) => {
    setIsPlaying(false);
    setActiveStep(stepId);
    const newCompleted = [];
    for (let i = 1; i < stepId; i++) {
      newCompleted.push(i);
    }
    setCompletedSteps(newCompleted);
  }, []);

  const handlePrevious = () => {
    if (activeStep > 1) {
      setIsPlaying(false);
      setActiveStep(activeStep - 1);
      setCompletedSteps(completedSteps.filter(s => s < activeStep - 1));
    }
  };

  const handleNext = () => {
    if (activeStep < totalSteps) {
      setCompletedSteps([...completedSteps, activeStep]);
      setActiveStep(activeStep + 1);
    }
  };

  const handleScenarioChange = (scenarioId) => {
    setActiveScenario(scenarioId);
    setActiveStep(1);
    setCompletedSteps([]);
    setIsPlaying(false);
  };

  const togglePlay = () => {
    if (!isPlaying && activeStep === totalSteps) {
      setActiveStep(1);
      setCompletedSteps([]);
    }
    setIsPlaying(!isPlaying);
  };

  const renderWorkspaceContent = () => {
    if (isIVF) {
      switch (activeStep) {
        case 1: return <IVFCoupleScreen scenario={scenario} isDark={isDark} />;
        case 2: return <IVFInvestigationsScreen scenario={scenario} isDark={isDark} />;
        case 3: return <IVFDiagnosisScreen scenario={scenario} isDark={isDark} />;
        case 4: return <IVFStimulationScreen scenario={scenario} isDark={isDark} />;
        case 5: return <IVFMonitoringScreen scenario={scenario} isDark={isDark} />;
        case 6: return <IVFRetrievalScreen scenario={scenario} isDark={isDark} />;
        case 7: return <IVFEmbryologyScreen scenario={scenario} isDark={isDark} />;
        case 8: return <IVFTransferScreen scenario={scenario} isDark={isDark} />;
        case 9: return <IVFPregnancyScreen scenario={scenario} isDark={isDark} />;
        case 10: return <IVFAnalyticsScreen scenario={scenario} isDark={isDark} />;
        default: return null;
      }
    }
    if (isRetina) {
      switch (activeStep) {
        case 1: return <RetinaScanScreen scenario={scenario} isDark={isDark} />;
        case 2: return <RetinaSegmentationScreen scenario={scenario} isDark={isDark} />;
        case 3: return <RetinaBiomarkerScreen scenario={scenario} isDark={isDark} />;
        case 4: return <RetinaFluidScreen scenario={scenario} isDark={isDark} />;
        case 5: return <RetinaLongitudinalScreen scenario={scenario} isDark={isDark} />;
        case 6: return <RetinaRiskScreen scenario={scenario} isDark={isDark} />;
        case 7: return <RetinaTreatmentScreen scenario={scenario} isDark={isDark} />;
        case 8: return <RetinaProgressScreen scenario={scenario} isDark={isDark} />;
        case 9: return <RetinaAdherenceScreen scenario={scenario} isDark={isDark} />;
        case 10: return <RetinaAnalyticsScreen scenario={scenario} isDark={isDark} />;
        default: return null;
      }
    }
    if (isSurgical) {
      // Surgical ERAS workflow screens
      switch (activeStep) {
        case 1:
          return <SurgicalTriageScreen scenario={scenario} isDark={isDark} />;
        case 2:
          return <PreopAssessmentScreen scenario={scenario} isDark={isDark} />;
        case 3:
          return <PrehabilitionScreen scenario={scenario} isDark={isDark} />;
        case 4:
          return <PatientEducationScreen scenario={scenario} isDark={isDark} />;
        case 5:
          return <DayOfSurgeryScreen scenario={scenario} isDark={isDark} />;
        case 6:
          return <IntraoperativeScreen scenario={scenario} isDark={isDark} />;
        case 7:
          return <PostopMonitoringScreen scenario={scenario} isDark={isDark} />;
        case 8:
          return <EarlyMobilisationScreen scenario={scenario} isDark={isDark} />;
        case 9:
          return <DischargePlanningScreen scenario={scenario} isDark={isDark} />;
        case 10:
          return <PostDischargeScreen scenario={scenario} isDark={isDark} />;
        case 11:
          return <SurgicalOutcomesScreen scenario={scenario} isDark={isDark} />;
        default:
          return null;
      }
    }
    
    // Standard outpatient workflow screens
    switch (activeStep) {
      case 1:
        return <VoiceCallScreen scenario={scenario} />;
      case 2:
        return <AppointmentScreen scenario={scenario} />;
      case 3:
        return <ConsultationScreen scenario={scenario} />;
      case 4:
        return <ClinicalEvidenceScreen scenario={scenario} />;
      case 5:
        return <ICDCodingScreen scenario={scenario} />;
      case 6:
        return <OrdersScreen scenario={scenario} />;
      case 7:
        return <FollowUpScreen scenario={scenario} />;
      case 8:
        return <RevenueScreen scenario={scenario} />;
      case 9:
        return <AnalyticsScreen scenario={scenario} />;
      default:
        return null;
    }
  };

  return (
    <section 
      id="simulation" 
      className={`section-padding ${isDark ? 'bg-slate-800' : 'bg-stone-50'}`}
      data-testid="simulation-section"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <span className={`inline-block px-4 py-1.5 rounded-full text-sm font-medium mb-4 ${
            isDark ? 'bg-sky-900/40 text-sky-400' : 'bg-sky-100 text-sky-700'
          }`}>
            Live Demo
          </span>
          <h2 className={`text-3xl sm:text-4xl lg:text-5xl font-semibold mb-4 tracking-tight ${
            isDark ? 'text-white' : 'text-slate-900'
          }`}>
            See SkyDoc Run a Full Clinical Episode
          </h2>
          <p className={`text-lg max-w-3xl mx-auto ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            {isIVF
              ? 'End-to-end AI-powered IVF workflow — from couple assessment to embryology, transfer, and pregnancy monitoring with 15+ specialized agents.'
              : isRetina
              ? 'AI-powered retinal analysis — from fundus and OCT capture to biomarker scoring, treatment intelligence, and longitudinal patient tracking.'
              : isSurgical 
              ? 'End-to-end surgical workflow automation aligned with ERAS protocols — from referral to recovery.'
              : 'From patient call to documentation, coding, orders, follow-up, revenue intelligence, and real-time analytics.'
            }
          </p>
        </div>

        {/* Scenario Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-6" data-testid="scenario-tabs">
          {Object.values(SCENARIOS).map((s) => (
            <button
              key={s.id}
              onClick={() => {
                if (s.id === 'retina' && !retinaUnlocked) {
                  setShowRetinaGate(true);
                } else if (s.id === 'ivf' && !ivfUnlocked) {
                  setShowIvfGate(true);
                } else {
                  handleScenarioChange(s.id);
                }
              }}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                activeScenario === s.id 
                  ? 'bg-sky-500 text-white shadow-md' 
                  : isDark 
                    ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' 
                    : 'bg-white text-slate-600 hover:bg-slate-100 shadow-sm'
              }`}
              data-testid={`scenario-tab-${s.id}`}
            >
              {s.name}
              {((s.id === 'retina' && !retinaUnlocked) || (s.id === 'ivf' && !ivfUnlocked)) && ' 🔒'}
            </button>
          ))}
        </div>

        {/* Retina Password Gate */}
        {showRetinaGate && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="retina-password-gate">
            <div className={`rounded-2xl shadow-2xl p-6 w-full max-w-sm mx-4 ${isDark ? 'bg-slate-800 text-white' : 'bg-white text-slate-900'}`}>
              <div className="flex items-center gap-2 mb-4">
                <Eye className="w-5 h-5 text-violet-500" />
                <h3 className="text-base font-bold">Retina AI Access</h3>
              </div>
              <p className={`text-xs mb-4 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                This demo is password-protected for select visitors.
              </p>
              <form onSubmit={handleRetinaAccess}>
                <input
                  type="password"
                  value={retinaPassword}
                  onChange={(e) => { setRetinaPassword(e.target.value); setRetinaPasswordError(false); }}
                  placeholder="Enter access code"
                  className={`w-full px-4 py-2.5 rounded-lg border text-sm mb-2 outline-none focus:ring-2 focus:ring-violet-500 ${
                    retinaPasswordError
                      ? 'border-rose-400 bg-rose-50 text-slate-900'
                      : isDark ? 'border-slate-600 bg-slate-700 text-white' : 'border-slate-200 bg-white text-slate-900'
                  }`}
                  autoFocus
                  data-testid="retina-password-input"
                />
                {retinaPasswordError && (
                  <p className="text-xs text-rose-500 mb-2">Incorrect password. Please try again.</p>
                )}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { setShowRetinaGate(false); setRetinaPassword(''); setRetinaPasswordError(false); }}
                    className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium ${isDark ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                    data-testid="retina-password-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 rounded-lg text-sm font-medium bg-violet-500 text-white hover:bg-violet-600"
                    data-testid="retina-password-submit"
                  >
                    Unlock
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* IVF Password Gate */}
        {showIvfGate && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="ivf-password-gate">
            <div className={`rounded-2xl shadow-2xl p-6 w-full max-w-sm mx-4 ${isDark ? 'bg-slate-800 text-white' : 'bg-white text-slate-900'}`}>
              <div className="flex items-center gap-2 mb-4">
                <Heart className="w-5 h-5 text-rose-500" />
                <h3 className="text-base font-bold">IVF Centre Access</h3>
              </div>
              <p className={`text-xs mb-4 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                This demo is password-protected for select visitors.
              </p>
              <form onSubmit={handleIvfAccess}>
                <input
                  type="password"
                  value={ivfPassword}
                  onChange={(e) => { setIvfPassword(e.target.value); setIvfPasswordError(false); }}
                  placeholder="Enter access code"
                  className={`w-full px-4 py-2.5 rounded-lg border text-sm mb-2 outline-none focus:ring-2 focus:ring-rose-500 ${
                    ivfPasswordError
                      ? 'border-rose-400 bg-rose-50 text-slate-900'
                      : isDark ? 'border-slate-600 bg-slate-700 text-white' : 'border-slate-200 bg-white text-slate-900'
                  }`}
                  autoFocus
                  data-testid="ivf-password-input"
                />
                {ivfPasswordError && (
                  <p className="text-xs text-rose-500 mb-2">Incorrect password. Please try again.</p>
                )}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { setShowIvfGate(false); setIvfPassword(''); setIvfPasswordError(false); }}
                    className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium ${isDark ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                    data-testid="ivf-password-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 rounded-lg text-sm font-medium bg-rose-500 text-white hover:bg-rose-600"
                    data-testid="ivf-password-submit"
                  >
                    Unlock
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ERAS Badge for Surgical */}
        {isSurgical && (
          <div className="flex justify-center mb-6">
            <div className={`inline-flex items-center gap-4 px-5 py-2.5 rounded-full ${
              isDark ? 'bg-emerald-900/30 border border-emerald-800' : 'bg-emerald-50 border border-emerald-200'
            }`}>
              <span className={`text-sm font-medium ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>
                ERAS-Optimised Workflow
              </span>
              <div className="flex items-center gap-3 text-xs">
                <span className={isDark ? 'text-emerald-400' : 'text-emerald-600'}>↓ Length of stay</span>
                <span className={isDark ? 'text-emerald-400' : 'text-emerald-600'}>↓ Complications</span>
                <span className={isDark ? 'text-emerald-400' : 'text-emerald-600'}>↑ Recovery speed</span>
              </div>
            </div>
          </div>
        )}

        {/* Retina AI Badge */}
        {isRetina && (
          <div className="flex justify-center mb-6">
            <div className={`inline-flex items-center gap-4 px-5 py-2.5 rounded-full ${
              isDark ? 'bg-violet-900/30 border border-violet-800' : 'bg-violet-50 border border-violet-200'
            }`}>
              <span className={`text-sm font-medium ${isDark ? 'text-violet-400' : 'text-violet-700'}`}>
                DR & DME Precision Care
              </span>
              <div className="flex items-center gap-3 text-xs">
                <span className={isDark ? 'text-violet-400' : 'text-violet-600'}>Fundus + OCT Fusion</span>
                <span className={isDark ? 'text-violet-400' : 'text-violet-600'}>Voxel Analysis</span>
                <span className={isDark ? 'text-violet-400' : 'text-violet-600'}>Predictive Intelligence</span>
              </div>
            </div>
          </div>
        )}

        {/* IVF Centre Badge */}
        {isIVF && (
          <div className="flex justify-center mb-6">
            <div className={`inline-flex items-center gap-4 px-5 py-2.5 rounded-full ${
              isDark ? 'bg-rose-900/30 border border-rose-800' : 'bg-rose-50 border border-rose-200'
            }`}>
              <span className={`text-sm font-medium ${isDark ? 'text-rose-400' : 'text-rose-700'}`}>
                IVF AI Workflow
              </span>
              <div className="flex items-center gap-3 text-xs">
                <span className={isDark ? 'text-rose-400' : 'text-rose-600'}>15+ AI Agents</span>
                <span className={isDark ? 'text-rose-400' : 'text-rose-600'}>Full Cycle Tracking</span>
                <span className={isDark ? 'text-rose-400' : 'text-rose-600'}>Embryology Intelligence</span>
              </div>
            </div>
          </div>
        )}

        {/* Progress bar */}
        <div className={`h-1.5 rounded-full mb-8 mx-auto max-w-2xl ${isDark ? 'bg-slate-700' : 'bg-slate-200'}`}>
          <div 
            className="h-full bg-sky-500 rounded-full transition-all duration-300" 
            style={{ width: `${(activeStep / totalSteps) * 100}%` }}
          />
        </div>

        {/* Main simulation container */}
        <div className={`rounded-2xl border overflow-hidden ${
          isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200 shadow-lg'
        }`}>
          <div className="grid lg:grid-cols-12">
            {/* Left Panel - Step Navigator */}
            <div className={`lg:col-span-4 xl:col-span-3 border-r p-4 lg:p-5 max-h-[700px] overflow-y-auto ${
              isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-stone-50 border-slate-200'
            }`}>
              <div className="space-y-1.5">
                {currentSteps.map((step, index) => {
                  const Icon = step.icon;
                  const isActive = activeStep === step.id;
                  const isCompleted = completedSteps.includes(step.id);
                  const showPhaseLabel = (isSurgical || isRetina || isIVF) && (index === 0 || currentSteps[index - 1].phase !== step.phase);

                  return (
                    <React.Fragment key={step.id}>
                      {/* Phase Label for Surgical */}
                      {showPhaseLabel && (
                        <div className={`px-2 py-1.5 mt-3 first:mt-0 ${
                          isDark ? 'text-slate-500' : 'text-slate-400'
                        }`}>
                          <span className="text-xs font-semibold uppercase tracking-wider">{step.phase}</span>
                        </div>
                      )}
                      <button
                        onClick={() => handleStepClick(step.id)}
                        className={`flex items-center gap-3 w-full text-left p-2.5 rounded-xl transition-all ${
                          isActive 
                            ? isDark ? 'bg-sky-900/40' : 'bg-sky-50' 
                            : isDark ? 'hover:bg-slate-700' : 'hover:bg-white'
                        }`}
                        data-testid={`step-${step.id}`}
                      >
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          isCompleted 
                            ? 'bg-emerald-500 text-white' 
                            : isActive 
                              ? 'bg-sky-500 text-white' 
                              : isDark 
                                ? 'bg-slate-700 text-slate-400' 
                                : 'bg-slate-200 text-slate-500'
                        }`}>
                          {isCompleted ? (
                            <Check className="w-4 h-4" />
                          ) : (
                            <Icon className="w-4 h-4" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium truncate ${
                            isActive 
                              ? isDark ? 'text-sky-400' : 'text-sky-600' 
                              : isCompleted 
                                ? isDark ? 'text-emerald-400' : 'text-emerald-600' 
                                : isDark ? 'text-slate-300' : 'text-slate-700'
                          }`}>
                            {step.title}
                          </p>
                          <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                            Step {step.id} of {totalSteps}
                          </p>
                        </div>
                      </button>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>

            {/* Right Panel - Dynamic Workspace */}
            <div className={`lg:col-span-8 xl:col-span-9 p-6 lg:p-8 min-h-[600px] max-h-[700px] overflow-y-auto ${
              isDark ? 'bg-slate-900' : ''
            }`}>
              <div className="animate-fade-in-up" key={`${activeScenario}-${activeStep}`}>
                {renderWorkspaceContent()}
              </div>
            </div>
          </div>

          {/* Bottom Controls */}
          <div className={`border-t px-6 py-4 ${
            isDark ? 'bg-slate-800 border-slate-700' : 'bg-stone-50 border-slate-200'
          }`}>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={handlePrevious}
                disabled={activeStep === 1}
                className={`p-2.5 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-colors ${
                  isDark ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-white text-slate-600 hover:bg-slate-100 shadow-sm'
                }`}
                data-testid="sim-prev-btn"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={togglePlay}
                className="px-6 py-2.5 bg-sky-500 text-white rounded-lg font-medium inline-flex items-center gap-2 hover:bg-sky-600 transition-colors"
                data-testid="sim-play-btn"
              >
                {isPlaying ? (
                  <>
                    <Pause className="w-4 h-4" />
                    Pause
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4" />
                    {activeStep === totalSteps ? 'Replay' : 'Autoplay'}
                  </>
                )}
              </button>
              <button
                onClick={handleNext}
                disabled={activeStep === totalSteps}
                className={`p-2.5 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-colors ${
                  isDark ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-white text-slate-600 hover:bg-slate-100 shadow-sm'
                }`}
                data-testid="sim-next-btn"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// =====================================================
// ANIMATED WRAPPER COMPONENTS FOR LIVE DEMO
// =====================================================

const AnimatedCard = ({ children, delay = 0, className = '' }) => (
  <div 
    className={`animate-fade-in-scale demo-card-hover ${className}`}
    style={{ animationDelay: `${delay}s`, opacity: 0, animationFillMode: 'forwards' }}
  >
    {children}
  </div>
);

const AnimatedBadge = ({ children, delay = 0 }) => (
  <span 
    className="animate-pop-in inline-block"
    style={{ animationDelay: `${delay}s`, opacity: 0, animationFillMode: 'forwards' }}
  >
    {children}
  </span>
);

const AnimatedListItem = ({ children, index = 0 }) => (
  <div 
    className="animate-fade-in-left"
    style={{ animationDelay: `${0.1 + index * 0.06}s`, opacity: 0, animationFillMode: 'forwards' }}
  >
    {children}
  </div>
);

const AnimatedHeader = ({ children, delay = 0 }) => (
  <div 
    className="animate-slide-down"
    style={{ animationDelay: `${delay}s`, opacity: 0, animationFillMode: 'forwards' }}
  >
    {children}
  </div>
);

const AnimatedMetric = ({ children, delay = 0 }) => (
  <div 
    className="animate-pop-in"
    style={{ animationDelay: `${delay}s`, opacity: 0, animationFillMode: 'forwards' }}
  >
    {children}
  </div>
);

// Step 1: Voice Call Screen
const VoiceCallScreen = ({ scenario }) => (
  <div className="space-y-6">
    <AnimatedHeader>
      <WorkspaceHeader 
        title="Incoming Call - Voice Agent Active"
        badge={{ text: 'AI Handling', color: 'success' }}
      />
    </AnimatedHeader>
    
    <div className="grid md:grid-cols-2 gap-6">
      <AnimatedCard delay={0.1}>
        <div className="mock-ui-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-sky-100 rounded-full flex items-center justify-center relative">
              <Phone className="w-6 h-6 text-sky-600" />
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-500 rounded-full animate-ping" />
            </div>
            <div>
              <p className="font-semibold text-slate-800">{scenario.patientName}</p>
              <p className="text-sm text-slate-500">Incoming call • {scenario.age} years old</p>
            </div>
          </div>
          <div className="space-y-3">
            <div className="p-3 bg-white rounded-lg border border-slate-200">
              <p className="text-sm text-slate-500 mb-1">Patient says:</p>
              <p className="text-slate-700 italic">"{scenario.symptoms}"</p>
            </div>
          </div>
        </div>
      </AnimatedCard>

      <AnimatedCard delay={0.2}>
        <div className="mock-ui-card">
          <p className="text-sm font-medium text-slate-700 mb-3">AI Triage Output</p>
          <div className="space-y-3">
            <AnimatedListItem index={0}>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Symptoms Detected</span>
                <AnimatedBadge delay={0.3}><span className="mock-ui-badge success">3 identified</span></AnimatedBadge>
              </div>
            </AnimatedListItem>
            <AnimatedListItem index={1}>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Red Flag Check</span>
                <AnimatedBadge delay={0.36}><span className="mock-ui-badge success">Clear</span></AnimatedBadge>
              </div>
            </AnimatedListItem>
            <AnimatedListItem index={2}>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Urgency Score</span>
                <AnimatedBadge delay={0.42}><span className="mock-ui-badge warning">Moderate</span></AnimatedBadge>
              </div>
            </AnimatedListItem>
            <AnimatedListItem index={3}>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Recommended</span>
                <AnimatedBadge delay={0.48}><span className="mock-ui-badge info">Same-day GP</span></AnimatedBadge>
              </div>
            </AnimatedListItem>
          </div>
        </div>
      </AnimatedCard>
    </div>

    <AnimatedCard delay={0.4}>
      <ValueStatement 
        action="SkyDoc captures symptoms, triages urgency, and routes the patient to the right care pathway."
        value="Reduced call handling time by 60%"
      />
    </AnimatedCard>
  </div>
);

// Step 2: Appointment Screen
const AppointmentScreen = ({ scenario }) => (
  <div className="space-y-6">
    <AnimatedHeader>
      <WorkspaceHeader 
        title="Smart Scheduling"
        badge={{ text: 'Auto-booked', color: 'success' }}
      />
    </AnimatedHeader>

    <AnimatedCard delay={0.1}>
      <div className="mock-ui-card">
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <AnimatedListItem index={0}>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Patient</p>
              <p className="font-semibold text-slate-800">{scenario.patientName}</p>
            </div>
          </AnimatedListItem>
          <AnimatedListItem index={1}>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Visit Type</p>
              <p className="font-semibold text-slate-800">{scenario.name}</p>
            </div>
          </AnimatedListItem>
          <AnimatedListItem index={2}>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Status</p>
              <AnimatedBadge delay={0.25}><span className="mock-ui-badge success">Confirmed</span></AnimatedBadge>
            </div>
          </AnimatedListItem>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <p className="text-sm font-medium text-slate-700 mb-3">Available Slots Today</p>
          <div className="grid grid-cols-4 gap-2">
            {['9:30 AM', '10:15 AM', '11:00 AM', '2:30 PM'].map((time, i) => (
              <AnimatedMetric key={time} delay={0.15 + i * 0.05}>
                <button
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all hover:scale-105 ${
                    i === 1 ? 'bg-sky-600 text-white shadow-lg shadow-sky-200' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {time}
                </button>
              </AnimatedMetric>
            ))}
          </div>
        </div>

        <div className="flex gap-2 mt-4">
          <AnimatedBadge delay={0.35}><span className="mock-ui-badge info">Same-day</span></AnimatedBadge>
          <AnimatedBadge delay={0.4}><span className="mock-ui-badge neutral">In-person</span></AnimatedBadge>
          <AnimatedBadge delay={0.45}><span className="mock-ui-badge success">Dr. assigned</span></AnimatedBadge>
        </div>
      </div>
    </AnimatedCard>

    <AnimatedCard delay={0.35}>
      <ValueStatement 
        action="SkyDoc books the appointment directly and updates the schedule without manual admin work."
        value="Zero admin intervention required"
      />
    </AnimatedCard>
  </div>
);

// Step 3: Consultation Screen
const ConsultationScreen = ({ scenario }) => (
  <div className="space-y-6">
    <AnimatedHeader>
      <WorkspaceHeader 
        title="Live Consultation Capture"
        badge={{ text: 'Recording', color: 'warning' }}
      />
    </AnimatedHeader>

    <div className="grid md:grid-cols-2 gap-6">
      <AnimatedCard delay={0.1}>
        <div className="mock-ui-card">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-medium text-slate-700">Live Transcript</p>
            <span className="flex items-center gap-1 text-xs text-red-500">
              <span className="w-2 h-2 bg-red-500 rounded-full animate-ping" />
              Recording
            </span>
          </div>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            <AnimatedListItem index={0}><TranscriptLine speaker="Doctor" text="What brings you in today?" /></AnimatedListItem>
            <AnimatedListItem index={1}><TranscriptLine speaker="Patient" text={scenario.symptoms} /></AnimatedListItem>
            <AnimatedListItem index={2}><TranscriptLine speaker="Doctor" text="How long have you had these symptoms?" /></AnimatedListItem>
            <AnimatedListItem index={3}><TranscriptLine speaker="Patient" text="About 3 days now, getting worse." /></AnimatedListItem>
          </div>
        </div>
      </AnimatedCard>

      <AnimatedCard delay={0.2}>
        <div className="mock-ui-card">
          <p className="text-sm font-medium text-slate-700 mb-3">Structured Note (SOAP)</p>
          <div className="space-y-3 text-sm">
            <AnimatedListItem index={0}>
              <div>
                <p className="text-xs font-semibold text-sky-600 uppercase">Subjective</p>
                <p className="text-slate-600">{scenario.symptoms}</p>
              </div>
            </AnimatedListItem>
            <AnimatedListItem index={1}>
              <div>
                <p className="text-xs font-semibold text-sky-600 uppercase">Objective</p>
                <p className="text-slate-600">Vitals pending examination</p>
              </div>
            </AnimatedListItem>
            <AnimatedListItem index={2}>
              <div>
                <p className="text-xs font-semibold text-sky-600 uppercase">Assessment</p>
                <p className="text-slate-600">{scenario.diagnosis}</p>
              </div>
            </AnimatedListItem>
            <AnimatedListItem index={3}>
              <div>
                <p className="text-xs font-semibold text-sky-600 uppercase">Plan</p>
                <p className="text-slate-600">See clinical recommendations</p>
              </div>
            </AnimatedListItem>
          </div>
        </div>
      </AnimatedCard>
    </div>

    <AnimatedCard delay={0.4}>
      <ValueStatement 
        action="During the consultation, SkyDoc turns conversation into structured clinical documentation."
        value="Documentation time reduced by 80%"
      />
    </AnimatedCard>
  </div>
);

// Step 4: Enhanced Clinical Evidence Screen
const ClinicalEvidenceScreen = ({ scenario }) => (
  <div className="space-y-6">
    <AnimatedHeader>
      <WorkspaceHeader 
        title="Evidence-Based Clinical Copilot"
        badge={{ text: 'AI + Evidence', color: 'info' }}
      />
    </AnimatedHeader>

    <div className="grid md:grid-cols-2 gap-6">
      {/* Differential Diagnosis */}
      <AnimatedCard delay={0.1}>
        <div className="mock-ui-card">
          <div className="flex items-center gap-2 mb-3">
            <Brain className="w-5 h-5 text-purple-600" />
            <p className="text-sm font-medium text-slate-700">Differential Diagnosis</p>
          </div>
          <div className="space-y-2">
            {scenario.differentials.map((dx, i) => (
              <AnimatedListItem key={i} index={i}>
                <DiagnosisItem 
                  name={dx.name}
                  confidence={dx.probability}
                  primary={i === 0}
                />
              </AnimatedListItem>
            ))}
          </div>
        </div>
      </AnimatedCard>

      {/* Evidence & Guidelines */}
      <div className="space-y-4">
        <AnimatedCard delay={0.2}>
          <div className="mock-ui-card">
            <div className="flex items-center gap-2 mb-3">
              <BookOpen className="w-5 h-5 text-sky-600" />
              <p className="text-sm font-medium text-slate-700">Clinical Guidelines</p>
            </div>
            <div className="space-y-2">
              {scenario.guidelines.map((guide, i) => (
                <AnimatedListItem key={i} index={i}>
                  <div className="flex items-center justify-between p-2 bg-sky-50 rounded-lg hover:bg-sky-100 transition-colors cursor-pointer">
                    <span className="text-sm text-sky-800">{guide}</span>
                    <ExternalLink className="w-4 h-4 text-sky-600" />
                  </div>
                </AnimatedListItem>
              ))}
            </div>
          </div>
        </AnimatedCard>

        <AnimatedCard delay={0.3}>
          <div className="mock-ui-card">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-4 h-4 text-amber-500" />
              <p className="text-sm font-medium text-slate-700">Red Flags to Rule Out</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {scenario.redFlags.map((flag, i) => (
                <AnimatedBadge key={i} delay={0.35 + i * 0.05}>
                  <span className="mock-ui-badge warning">{flag}</span>
                </AnimatedBadge>
              ))}
            </div>
          </div>
        </AnimatedCard>
      </div>
    </div>

    {/* Evidence-Based Recommendations */}
    <AnimatedCard delay={0.4}>
      <div className="mock-ui-card bg-gradient-to-r from-sky-50 to-purple-50 border-sky-200">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-sky-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <Lightbulb className="w-5 h-5 text-sky-600" />
          </div>
          <div className="flex-1">
            <p className="font-medium text-slate-800 mb-2">AI Clinical Reasoning</p>
            <p className="text-sm text-slate-600 mb-3">
              Based on the patient's symptoms ({scenario.symptoms}) and clinical context, 
              SkyDoc recommends following the <strong>{scenario.guidelines[0]}</strong> pathway.
            </p>
            <div className="flex flex-wrap gap-2">
              <AnimatedBadge delay={0.5}>
                <span className="px-3 py-1 bg-white rounded-full text-xs font-medium text-sky-700 border border-sky-200">
                  Evidence Level: High
                </span>
              </AnimatedBadge>
              <AnimatedBadge delay={0.55}>
                <span className="px-3 py-1 bg-white rounded-full text-xs font-medium text-emerald-700 border border-emerald-200">
                  Protocol Compliant
                </span>
              </AnimatedBadge>
            </div>
          </div>
        </div>
      </div>
    </AnimatedCard>

    {/* Real-time Prompts */}
    <div className="mock-ui-card">
      <p className="text-sm font-medium text-slate-700 mb-3">Suggested Examination Points</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {['Vital signs', 'Chest auscultation', 'Throat examination', 'Lymph nodes'].map((item) => (
          <div key={item} className="p-2 bg-slate-50 rounded-lg text-center">
            <p className="text-xs text-slate-600">{item}</p>
          </div>
        ))}
      </div>
    </div>

    <ValueStatement 
      action="SkyDoc provides real-time evidence-based support using trusted clinical protocols (NICE, WHO, ESC, ADA)."
      value="Guideline adherence improved to 94%"
      review="Doctor confirms clinical judgment"
    />
  </div>
);

// Step 5: ICD Coding Screen
const ICDCodingScreen = ({ scenario }) => (
  <div className="space-y-6">
    <WorkspaceHeader 
      title="Automated ICD-10 Coding"
      badge={{ text: 'Generated', color: 'success' }}
    />

    <div className="mock-ui-card">
      <p className="text-sm font-medium text-slate-700 mb-4">Suggested ICD-10 Codes</p>
      <div className="space-y-3">
        {scenario.icdCodes.map((code, index) => (
          <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200">
            <div className="flex items-center gap-3">
              <code className="text-sm font-mono text-blue-600 bg-blue-50 px-2 py-1 rounded">
                {code.split(' ')[0]}
              </code>
              <span className="text-sm text-slate-700">{code.split(' ').slice(1).join(' ')}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="mock-ui-badge success">High confidence</span>
              <input type="checkbox" defaultChecked className="w-4 h-4 rounded border-slate-300 text-blue-600" />
            </div>
          </div>
        ))}
      </div>
    </div>

    <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
      <div className="flex items-start gap-3">
        <User className="w-5 h-5 text-amber-600 mt-0.5" />
        <div>
          <p className="font-medium text-amber-800">Clinician Review Required</p>
          <p className="text-sm text-amber-700">Please confirm or modify codes before submission</p>
        </div>
      </div>
    </div>

    <ValueStatement 
      action="SkyDoc converts clinical context into coding suggestions, reducing missed documentation."
      value="Billing accuracy improved by 35%"
      review="Clinician approves before submission"
    />
  </div>
);

// Step 6: Orders Screen
const OrdersScreen = ({ scenario }) => (
  <div className="space-y-6">
    <WorkspaceHeader 
      title="Orders & Prescriptions"
      badge={{ text: 'Pending Approval', color: 'warning' }}
    />

    <div className="grid md:grid-cols-2 gap-6">
      <div className="mock-ui-card">
        <div className="flex items-center gap-2 mb-3">
          <Activity className="w-5 h-5 text-emerald-600" />
          <p className="text-sm font-medium text-slate-700">Lab Orders</p>
        </div>
        <div className="space-y-2">
          {scenario.labs.map((lab, index) => (
            <div key={index} className="flex items-center justify-between p-2 bg-white rounded border border-slate-200">
              <span className="text-sm text-slate-700">{lab}</span>
              <span className="mock-ui-badge info">Queued</span>
            </div>
          ))}
        </div>
      </div>

      <div className="mock-ui-card">
        <div className="flex items-center gap-2 mb-3">
          <Pill className="w-5 h-5 text-rose-600" />
          <p className="text-sm font-medium text-slate-700">Prescriptions</p>
        </div>
        <div className="space-y-2">
          {scenario.medications.map((med, index) => (
            <div key={index} className="flex items-center justify-between p-2 bg-white rounded border border-slate-200">
              <span className="text-sm text-slate-700">{med}</span>
              <span className="mock-ui-badge warning">Draft</span>
            </div>
          ))}
        </div>
      </div>
    </div>

    <div className="flex items-center justify-between p-4 bg-blue-50 border border-blue-200 rounded-xl">
      <div className="flex items-center gap-3">
        <Shield className="w-5 h-5 text-blue-600" />
        <span className="text-sm text-blue-800">EMR Integration Ready</span>
      </div>
      <button className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium">
        Approve & Send
      </button>
    </div>

    <ValueStatement 
      action="SkyDoc prepares downstream actions so care moves forward without repeated manual entry."
      value="Order preparation time reduced to seconds"
      review="Doctor approves before order submission"
    />
  </div>
);

// Step 7: Follow-up Screen
const FollowUpScreen = ({ scenario }) => (
  <div className="space-y-6">
    <WorkspaceHeader 
      title="Follow-up Automation"
      badge={{ text: 'Scheduled', color: 'success' }}
    />

    <div className="mock-ui-card">
      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <div className="text-center p-4 bg-white rounded-lg border border-slate-200">
          <Clock className="w-8 h-8 text-blue-600 mx-auto mb-2" />
          <p className="text-2xl font-bold text-slate-800">{scenario.followUp}</p>
          <p className="text-sm text-slate-500">Follow-up in</p>
        </div>
        <div className="text-center p-4 bg-white rounded-lg border border-slate-200">
          <Bell className="w-8 h-8 text-amber-600 mx-auto mb-2" />
          <p className="text-2xl font-bold text-slate-800">3</p>
          <p className="text-sm text-slate-500">Reminders set</p>
        </div>
        <div className="text-center p-4 bg-white rounded-lg border border-slate-200">
          <Check className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
          <p className="text-2xl font-bold text-slate-800">Auto</p>
          <p className="text-sm text-slate-500">Outreach mode</p>
        </div>
      </div>

      <div className="space-y-3">
        <p className="text-sm font-medium text-slate-700">Scheduled Communications</p>
        <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200">
          <span className="text-sm text-slate-700">SMS reminder - 1 day before</span>
          <span className="mock-ui-badge success">Scheduled</span>
        </div>
        <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200">
          <span className="text-sm text-slate-700">Call reminder - Morning of</span>
          <span className="mock-ui-badge success">Scheduled</span>
        </div>
        <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200">
          <span className="text-sm text-slate-700">Outcome check - Post visit</span>
          <span className="mock-ui-badge info">Planned</span>
        </div>
      </div>
    </div>

    <ValueStatement 
      action="SkyDoc closes the loop with reminders, rescheduling, and follow-up coordination."
      value="No-show rate reduced by 40%"
    />
  </div>
);

// Step 8: Revenue Screen
const RevenueScreen = ({ scenario }) => (
  <div className="space-y-6">
    <WorkspaceHeader 
      title="Revenue & Billing Intelligence"
      badge={{ text: 'Claim Ready', color: 'success' }}
    />

    <div className="grid md:grid-cols-3 gap-4 mb-6">
      <div className="text-center p-6 bg-emerald-50 rounded-xl border border-emerald-200">
        <p className="text-3xl font-bold text-emerald-600">100%</p>
        <p className="text-sm text-emerald-700">Documentation Complete</p>
      </div>
      <div className="text-center p-6 bg-blue-50 rounded-xl border border-blue-200">
        <p className="text-3xl font-bold text-blue-600">{scenario.icdCodes.length}</p>
        <p className="text-sm text-blue-700">Codes Captured</p>
      </div>
      <div className="text-center p-6 bg-purple-50 rounded-xl border border-purple-200">
        <p className="text-3xl font-bold text-purple-600">Ready</p>
        <p className="text-sm text-purple-700">Claim Status</p>
      </div>
    </div>

    <div className="mock-ui-card">
      <p className="text-sm font-medium text-slate-700 mb-4">Episode Financial Summary</p>
      <div className="space-y-3">
        <div className="flex items-center justify-between py-2 border-b border-slate-100">
          <span className="text-slate-600">Diagnosis documented</span>
          <Check className="w-5 h-5 text-emerald-500" />
        </div>
        <div className="flex items-center justify-between py-2 border-b border-slate-100">
          <span className="text-slate-600">Procedure/test linkage</span>
          <Check className="w-5 h-5 text-emerald-500" />
        </div>
        <div className="flex items-center justify-between py-2 border-b border-slate-100">
          <span className="text-slate-600">Medication traceability</span>
          <Check className="w-5 h-5 text-emerald-500" />
        </div>
        <div className="flex items-center justify-between py-2 border-b border-slate-100">
          <span className="text-slate-600">Medical necessity summary</span>
          <Check className="w-5 h-5 text-emerald-500" />
        </div>
        <div className="flex items-center justify-between py-2">
          <span className="text-slate-600 font-medium">Revenue opportunity captured</span>
          <span className="mock-ui-badge success">Optimized</span>
        </div>
      </div>
    </div>

    <ValueStatement 
      action="SkyDoc links clinical activity to financial outcomes with structured, defendable documentation."
      value="Revenue leakage eliminated"
      highlight
    />
  </div>
);

// Step 9: NEW Analytics & Intelligence Studio Screen
const AnalyticsScreen = ({ scenario }) => {
  const [analyticsView, setAnalyticsView] = useState('operational');
  
  return (
    <div className="space-y-6">
      <WorkspaceHeader 
        title="Analytics & Intelligence Studio"
        badge={{ text: 'Real-time', color: 'info' }}
      />

      {/* Analytics View Toggle */}
      <div className="flex gap-2 p-1 bg-slate-100 rounded-lg w-fit">
        <button
          onClick={() => setAnalyticsView('operational')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
            analyticsView === 'operational' ? 'bg-white shadow-sm text-blue-600' : 'text-slate-600'
          }`}
        >
          Operational
        </button>
        <button
          onClick={() => setAnalyticsView('clinical')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
            analyticsView === 'clinical' ? 'bg-white shadow-sm text-blue-600' : 'text-slate-600'
          }`}
        >
          Clinical Audit
        </button>
        <button
          onClick={() => setAnalyticsView('research')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
            analyticsView === 'research' ? 'bg-white shadow-sm text-blue-600' : 'text-slate-600'
          }`}
        >
          Research
        </button>
      </div>

      {analyticsView === 'operational' && (
        <OperationalAnalytics scenario={scenario} />
      )}
      
      {analyticsView === 'clinical' && (
        <ClinicalAuditAnalytics scenario={scenario} />
      )}
      
      {analyticsView === 'research' && (
        <ResearchAnalytics scenario={scenario} />
      )}

      <ValueStatement 
        action="SkyDoc Intelligence Studio turns clinical data into actionable insights for operations, audit, and research."
        value="Complete visibility across your clinical workflow"
        highlight
      />
    </div>
  );
};

// Operational Analytics Sub-component
const OperationalAnalytics = ({ scenario }) => (
  <div className="space-y-6">
    {/* KPI Cards */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <MetricCard 
        label="Consultations Today"
        value={scenario.analytics.consultToday}
        trend="+12%"
        trendUp={true}
      />
      <MetricCard 
        label="Avg. Duration"
        value={scenario.analytics.avgDuration}
        trend="-2 min"
        trendUp={true}
      />
      <MetricCard 
        label="Doc Completeness"
        value={`${scenario.analytics.docCompleteness}%`}
        trend="+3%"
        trendUp={true}
      />
      <MetricCard 
        label="AI Acceptance Rate"
        value="89%"
        trend="+5%"
        trendUp={true}
      />
    </div>

    {/* Charts Row */}
    <div className="grid md:grid-cols-2 gap-6">
      <div className="mock-ui-card">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm font-medium text-slate-700">Consultation Volume (7 days)</p>
          <BarChart3 className="w-4 h-4 text-slate-400" />
        </div>
        <div className="flex items-end gap-1 h-32">
          {[45, 52, 48, 61, 55, 47, 58].map((val, i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-1">
              <div 
                className="w-full bg-blue-500 rounded-t"
                style={{ height: `${(val/61) * 100}%` }}
              />
              <span className="text-xs text-slate-400">{['M','T','W','T','F','S','S'][i]}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="mock-ui-card">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm font-medium text-slate-700">Doctor Productivity</p>
          <TrendingUp className="w-4 h-4 text-emerald-500" />
        </div>
        <div className="space-y-3">
          <ProductivityBar label="Dr. Chen" value={92} color="emerald" />
          <ProductivityBar label="Dr. Smith" value={87} color="blue" />
          <ProductivityBar label="Dr. Patel" value={78} color="amber" />
        </div>
      </div>
    </div>

    {/* Real-time Feed */}
    <div className="mock-ui-card">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-medium text-slate-700">Live Activity Feed</p>
        <span className="flex items-center gap-1 text-xs text-emerald-600">
          <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
          Live
        </span>
      </div>
      <div className="space-y-2 max-h-32 overflow-y-auto">
        <ActivityItem time="Just now" text="Consultation completed - Dr. Chen" type="success" />
        <ActivityItem time="2 min ago" text="Lab order sent - CBC, CRP" type="info" />
        <ActivityItem time="5 min ago" text="Follow-up scheduled - Patient" type="neutral" />
        <ActivityItem time="8 min ago" text="ICD codes approved - 3 codes" type="success" />
      </div>
    </div>
  </div>
);

// Clinical Audit Analytics Sub-component
const ClinicalAuditAnalytics = ({ scenario }) => (
  <div className="space-y-6">
    {/* Audit KPIs */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <MetricCard 
        label="Guideline Adherence"
        value={`${scenario.analytics.guidelineAdherence}%`}
        trend="+4%"
        trendUp={true}
      />
      <MetricCard 
        label="Red Flags Captured"
        value="100%"
        trend="—"
        trendUp={null}
      />
      <MetricCard 
        label="Documentation Score"
        value="A+"
        trend="+1 grade"
        trendUp={true}
      />
      <MetricCard 
        label="Safety Events"
        value="0"
        trend="—"
        trendUp={null}
      />
    </div>

    {/* Clinical Quality */}
    <div className="grid md:grid-cols-2 gap-6">
      <div className="mock-ui-card">
        <div className="flex items-center gap-2 mb-4">
          <FileCheck className="w-5 h-5 text-emerald-600" />
          <p className="text-sm font-medium text-slate-700">Protocol Compliance</p>
        </div>
        <div className="space-y-3">
          <ComplianceItem label="Antibiotic prescribing" value={95} status="compliant" />
          <ComplianceItem label="Red flag documentation" value={100} status="compliant" />
          <ComplianceItem label="Follow-up scheduling" value={88} status="warning" />
          <ComplianceItem label="Lab ordering guidelines" value={92} status="compliant" />
        </div>
      </div>

      <div className="mock-ui-card">
        <div className="flex items-center gap-2 mb-4">
          <Database className="w-5 h-5 text-purple-600" />
          <p className="text-sm font-medium text-slate-700">Top Diagnoses This Month</p>
        </div>
        <div className="space-y-2">
          {[
            { name: 'Acute pharyngitis', count: 145 },
            { name: 'Hypertension', count: 132 },
            { name: 'Type 2 Diabetes', count: 98 },
            { name: 'URTI', count: 87 }
          ].map((dx, i) => (
            <div key={i} className="flex items-center justify-between p-2 bg-slate-50 rounded">
              <span className="text-sm text-slate-700">{dx.name}</span>
              <span className="text-sm font-medium text-slate-600">{dx.count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* Care Variation Alert */}
    <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
      <div className="flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
        <div>
          <p className="font-medium text-amber-800">Care Variation Detected</p>
          <p className="text-sm text-amber-700">
            Antibiotic duration varies by 2.5 days across clinicians for similar cases. 
            Consider standardization.
          </p>
        </div>
      </div>
    </div>
  </div>
);

// Research Analytics Sub-component
const ResearchAnalytics = ({ scenario }) => (
  <div className="space-y-6">
    {/* Conversational Query */}
    <div className="mock-ui-card bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200">
      <div className="flex items-center gap-2 mb-3">
        <Search className="w-5 h-5 text-purple-600" />
        <p className="text-sm font-medium text-slate-700">Ask SkyDoc Intelligence</p>
      </div>
      <div className="flex gap-2">
        <input 
          type="text" 
          placeholder="e.g., Show diabetic patients with HbA1c > 8% in last 6 months..."
          className="flex-1 px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm"
        />
        <button className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium">
          Query
        </button>
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        <span className="px-2 py-1 bg-white rounded text-xs text-slate-600 cursor-pointer hover:bg-slate-50">
          Cohort analysis
        </span>
        <span className="px-2 py-1 bg-white rounded text-xs text-slate-600 cursor-pointer hover:bg-slate-50">
          Outcome trends
        </span>
        <span className="px-2 py-1 bg-white rounded text-xs text-slate-600 cursor-pointer hover:bg-slate-50">
          Treatment comparison
        </span>
      </div>
    </div>

    {/* Research Capabilities */}
    <div className="grid md:grid-cols-3 gap-4">
      <div className="mock-ui-card text-center">
        <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-3">
          <PieChart className="w-6 h-6 text-blue-600" />
        </div>
        <p className="font-medium text-slate-800">Cohort Builder</p>
        <p className="text-xs text-slate-500 mt-1">Define patient groups by any variable</p>
      </div>
      <div className="mock-ui-card text-center">
        <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-3">
          <BarChart3 className="w-6 h-6 text-purple-600" />
        </div>
        <p className="font-medium text-slate-800">Statistical Analysis</p>
        <p className="text-xs text-slate-500 mt-1">Chi-square, t-test, regression</p>
      </div>
      <div className="mock-ui-card text-center">
        <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mx-auto mb-3">
          <FileText className="w-6 h-6 text-emerald-600" />
        </div>
        <p className="font-medium text-slate-800">Export & Reports</p>
        <p className="text-xs text-slate-500 mt-1">CSV, Excel, audit-ready</p>
      </div>
    </div>

    {/* Sample Query Result */}
    <div className="mock-ui-card">
      <p className="text-sm font-medium text-slate-700 mb-3">Sample Query Result</p>
      <div className="p-4 bg-slate-50 rounded-lg">
        <p className="text-sm text-slate-600 mb-2">
          <strong>Query:</strong> "Diabetic patients with poor glycaemic control on metformin"
        </p>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-2xl font-bold text-blue-600">234</p>
            <p className="text-xs text-slate-500">Patients matched</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-amber-600">8.7%</p>
            <p className="text-xs text-slate-500">Avg HbA1c</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-emerald-600">67%</p>
            <p className="text-xs text-slate-500">Follow-up rate</p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Helper Components
const WorkspaceHeader = ({ title, badge }) => (
  <div className="flex items-center justify-between mb-4">
    <h3 className="text-xl font-bold text-slate-800">{title}</h3>
    <span className={`mock-ui-badge ${badge.color}`}>{badge.text}</span>
  </div>
);

const ValueStatement = ({ action, value, review, highlight }) => (
  <div className={`p-4 rounded-xl ${highlight ? 'bg-emerald-50 border border-emerald-200' : 'bg-slate-50'}`}>
    <div className="flex items-start gap-3">
      <Stethoscope className={`w-5 h-5 mt-0.5 flex-shrink-0 ${highlight ? 'text-emerald-600' : 'text-blue-600'}`} />
      <div className="space-y-1">
        <p className="text-sm text-slate-700">{action}</p>
        <p className={`text-sm font-semibold ${highlight ? 'text-emerald-700' : 'text-blue-700'}`}>{value}</p>
        {review && (
          <p className="text-xs text-slate-500 flex items-center gap-1">
            <User className="w-3 h-3" /> {review}
          </p>
        )}
      </div>
    </div>
  </div>
);

const TranscriptLine = ({ speaker, text }) => (
  <div className={`p-2 rounded-lg ${speaker === 'Doctor' ? 'bg-blue-50' : 'bg-slate-100'}`}>
    <p className="text-xs font-medium text-slate-500 mb-1">{speaker}</p>
    <p className="text-sm text-slate-700">{text}</p>
  </div>
);

const DiagnosisItem = ({ name, confidence, primary }) => (
  <div className={`p-3 rounded-lg border ${primary ? 'bg-purple-50 border-purple-200' : 'bg-white border-slate-200'}`}>
    <div className="flex items-center justify-between mb-2">
      <span className={`text-sm font-medium ${primary ? 'text-purple-800' : 'text-slate-700'}`}>{name}</span>
      <span className={`text-xs ${primary ? 'text-purple-600' : 'text-slate-500'}`}>{confidence}%</span>
    </div>
    <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
      <div 
        className={`h-full rounded-full ${primary ? 'bg-purple-500' : 'bg-slate-400'}`}
        style={{ width: `${confidence}%` }}
      />
    </div>
  </div>
);

const MetricCard = ({ label, value, trend, trendUp }) => (
  <div className="p-4 bg-white rounded-xl border border-slate-200">
    <p className="text-xs text-slate-500 mb-1">{label}</p>
    <p className="text-2xl font-bold text-slate-800">{value}</p>
    <div className="flex items-center gap-1 mt-1">
      {trendUp === true && <ArrowUpRight className="w-3 h-3 text-emerald-500" />}
      {trendUp === false && <ArrowDownRight className="w-3 h-3 text-red-500" />}
      {trendUp === null && <Minus className="w-3 h-3 text-slate-400" />}
      <span className={`text-xs ${trendUp === true ? 'text-emerald-600' : trendUp === false ? 'text-red-600' : 'text-slate-400'}`}>
        {trend}
      </span>
    </div>
  </div>
);

const ProductivityBar = ({ label, value, color }) => (
  <div>
    <div className="flex items-center justify-between mb-1">
      <span className="text-sm text-slate-600">{label}</span>
      <span className="text-sm font-medium text-slate-700">{value}%</span>
    </div>
    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
      <div 
        className={`h-full rounded-full bg-${color}-500`}
        style={{ width: `${value}%`, backgroundColor: color === 'emerald' ? '#10b981' : color === 'blue' ? '#3b82f6' : '#f59e0b' }}
      />
    </div>
  </div>
);

const ActivityItem = ({ time, text, type }) => (
  <div className="flex items-center gap-3 text-sm">
    <span className={`w-2 h-2 rounded-full ${
      type === 'success' ? 'bg-emerald-500' : type === 'info' ? 'bg-blue-500' : 'bg-slate-400'
    }`} />
    <span className="text-slate-600 flex-1">{text}</span>
    <span className="text-xs text-slate-400">{time}</span>
  </div>
);

const ComplianceItem = ({ label, value, status }) => (
  <div className="flex items-center justify-between">
    <span className="text-sm text-slate-600">{label}</span>
    <div className="flex items-center gap-2">
      <span className="text-sm font-medium">{value}%</span>
      <span className={`w-2 h-2 rounded-full ${status === 'compliant' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
    </div>
  </div>
);

// =====================================================
// SURGICAL ERAS WORKFLOW SCREENS
// =====================================================

// Step 1: Surgical Triage Screen
const SurgicalTriageScreen = ({ scenario, isDark }) => (
  <div className="space-y-6">
    <WorkspaceHeader 
      title="Surgical Triage & Referral"
      badge={{ text: 'AI Triage', color: 'info' }}
    />
    
    <div className="grid md:grid-cols-2 gap-6">
      <div className="mock-ui-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-sky-100 rounded-full flex items-center justify-center">
            <Phone className="w-6 h-6 text-sky-600" />
          </div>
          <div>
            <p className="font-semibold text-slate-800">{scenario.patientName}</p>
            <p className="text-sm text-slate-500">{scenario.age} years • Surgical Referral</p>
          </div>
        </div>
        <div className="p-3 bg-white rounded-lg border border-slate-200">
          <p className="text-sm text-slate-500 mb-1">Presenting Complaint:</p>
          <p className="text-slate-700">{scenario.symptoms}</p>
        </div>
      </div>

      <div className="mock-ui-card">
        <p className="text-sm font-medium text-slate-700 mb-3">AI Triage Assessment</p>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-600">Surgical Indication</span>
            <span className="mock-ui-badge success">Confirmed</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-600">Urgency Level</span>
            <span className="mock-ui-badge warning">Semi-urgent</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-600">Pathway Allocation</span>
            <span className="mock-ui-badge info">ERAS Protocol</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-600">Recommended Procedure</span>
            <span className="text-sm font-medium text-slate-800">{scenario.procedure}</span>
          </div>
        </div>
      </div>
    </div>

    <ERASAlignmentBadge 
      alignment="Early identification and pathway allocation"
      benefit="Reduces time-to-surgery by prioritising appropriate cases"
    />

    <ValueStatement 
      action="SkyDoc captures referral details, assesses surgical indication, and allocates to the ERAS pathway."
      value="Time-to-triage reduced by 70%"
    />
  </div>
);

// Step 2: Pre-op Assessment Screen
const PreopAssessmentScreen = ({ scenario, isDark }) => (
  <div className="space-y-6">
    <WorkspaceHeader 
      title="Preoperative Assessment"
      badge={{ text: 'Risk Scoring', color: 'warning' }}
    />

    <div className="grid md:grid-cols-2 gap-6">
      {/* Risk Scores */}
      <div className="mock-ui-card">
        <div className="flex items-center gap-2 mb-4">
          <AlertTriangle className="w-5 h-5 text-amber-500" />
          <p className="text-sm font-medium text-slate-700">Risk Stratification</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <RiskScoreBadge label="ASA Score" value={scenario.riskScore.asa} color="blue" />
          <RiskScoreBadge label="Frailty" value={scenario.riskScore.frailty} color="emerald" />
          <RiskScoreBadge label="Surgical Risk" value={scenario.riskScore.surgical} color="amber" />
          <RiskScoreBadge label="VTE Risk" value={scenario.riskScore.vte} color="purple" />
        </div>
      </div>

      {/* Comorbidity Capture */}
      <div className="mock-ui-card">
        <p className="text-sm font-medium text-slate-700 mb-3">Comorbidities Identified</p>
        <div className="space-y-2">
          <div className="flex items-center justify-between p-2 bg-white rounded border border-slate-200">
            <span className="text-sm text-slate-700">Hypertension (controlled)</span>
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
          </div>
          <div className="flex items-center justify-between p-2 bg-white rounded border border-slate-200">
            <span className="text-sm text-slate-700">Type 2 Diabetes</span>
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
          </div>
          <div className="flex items-center justify-between p-2 bg-white rounded border border-slate-200">
            <span className="text-sm text-slate-700">BMI: 28.5 (Overweight)</span>
            <AlertCircle className="w-4 h-4 text-amber-500" />
          </div>
        </div>
      </div>
    </div>

    {/* Labs Required */}
    <div className="mock-ui-card">
      <p className="text-sm font-medium text-slate-700 mb-3">Pre-op Investigations Ordered</p>
      <div className="flex flex-wrap gap-2">
        {scenario.labs.map((lab, i) => (
          <span key={i} className="px-3 py-1.5 bg-sky-50 text-sky-700 rounded-lg text-sm font-medium">
            {lab}
          </span>
        ))}
      </div>
    </div>

    <ERASAlignmentBadge 
      alignment="Optimisation before surgery"
      benefit="Identifies modifiable risk factors for prehabilitation"
    />

    <ValueStatement 
      action="SkyDoc structures pre-op clinic notes, captures comorbidities, and calculates risk scores automatically."
      value="Risk assessment accuracy improved by 40%"
    />
  </div>
);

// Step 3: Prehabilitation Screen
const PrehabilitionScreen = ({ scenario, isDark }) => (
  <div className="space-y-6">
    <WorkspaceHeader 
      title="Prehabilitation & Optimization"
      badge={{ text: 'ERAS Protocol', color: 'success' }}
    />

    <div className="grid md:grid-cols-3 gap-4">
      <PrehbCard 
        icon={Heart}
        title="Cardiovascular"
        status="Optimised"
        items={['BP controlled', 'ECG reviewed', 'Exercise tolerance OK']}
        color="rose"
      />
      <PrehbCard 
        icon={Activity}
        title="Nutrition"
        status="In Progress"
        items={['Carb loading advised', 'Protein supplementation', 'Clear fluids 2h pre-op']}
        color="amber"
      />
      <PrehbCard 
        icon={Stethoscope}
        title="Lifestyle"
        status="Advised"
        items={['Smoking cessation', 'Alcohol reduction', 'Mobility exercises']}
        color="emerald"
      />
    </div>

    <div className="mock-ui-card bg-gradient-to-r from-emerald-50 to-sky-50 border-emerald-200">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
          <Timer className="w-5 h-5 text-emerald-600" />
        </div>
        <div>
          <p className="font-medium text-slate-800 mb-1">ERAS Prehab Checklist</p>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <span className="flex items-center gap-1 text-emerald-700"><Check className="w-3 h-3" /> Anaemia screening</span>
            <span className="flex items-center gap-1 text-emerald-700"><Check className="w-3 h-3" /> Diabetic optimisation</span>
            <span className="flex items-center gap-1 text-emerald-700"><Check className="w-3 h-3" /> VTE risk assessed</span>
            <span className="flex items-center gap-1 text-amber-600"><Clock className="w-3 h-3" /> Fitness for surgery</span>
          </div>
        </div>
      </div>
    </div>

    <ERASAlignmentBadge 
      alignment="Patient optimization before surgery"
      benefit="Reduces complications and accelerates recovery"
    />

    <ValueStatement 
      action="SkyDoc generates personalized prehab plans and tracks optimization milestones."
      value="Complication risk reduced by 30%"
    />
  </div>
);

// Step 4: Patient Education Screen
const PatientEducationScreen = ({ scenario, isDark }) => (
  <div className="space-y-6">
    <WorkspaceHeader 
      title="Patient Education & Consent"
      badge={{ text: 'Prepared', color: 'success' }}
    />

    <div className="grid md:grid-cols-2 gap-6">
      <div className="mock-ui-card">
        <p className="text-sm font-medium text-slate-700 mb-3">AI-Generated Patient Materials</p>
        <div className="space-y-3">
          <EducationItem title="Procedure Overview" desc="Plain-language explanation of laparoscopic surgery" sent />
          <EducationItem title="What to Expect" desc="Day-by-day recovery timeline" sent />
          <EducationItem title="Pre-op Instructions" desc="Fasting, medication, arrival time" sent />
          <EducationItem title="Post-op Care" desc="Wound care, activity, warning signs" pending />
        </div>
      </div>

      <div className="mock-ui-card">
        <p className="text-sm font-medium text-slate-700 mb-3">Consent Documentation</p>
        <div className="space-y-3">
          <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-emerald-800">Procedure: {scenario.procedure}</span>
              <span className="mock-ui-badge success">Ready</span>
            </div>
          </div>
          <div className="text-sm text-slate-600 space-y-1">
            <p>• Risks and benefits documented</p>
            <p>• Alternative options discussed</p>
            <p>• Patient questions answered</p>
            <p>• Signature pending</p>
          </div>
        </div>
      </div>
    </div>

    <ERASAlignmentBadge 
      alignment="Patient engagement and expectation setting"
      benefit="Reduces anxiety and improves compliance with recovery protocols"
    />

    <ValueStatement 
      action="SkyDoc auto-generates patient education materials and supports consent documentation."
      value="Patient satisfaction scores improved by 25%"
    />
  </div>
);

// Step 5: Day of Surgery Screen
const DayOfSurgeryScreen = ({ scenario, isDark }) => (
  <div className="space-y-6">
    <WorkspaceHeader 
      title="Day of Surgery Coordination"
      badge={{ text: 'Theatre Ready', color: 'success' }}
    />

    <div className="mock-ui-card">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm font-medium text-slate-700">Surgical Safety Checklist</p>
        <span className="text-xs text-emerald-600 font-medium">WHO Checklist Compliant</span>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <ChecklistSection 
          title="Sign In" 
          items={['Patient identity', 'Site marked', 'Consent signed', 'Allergies confirmed']}
          completed={4}
          total={4}
        />
        <ChecklistSection 
          title="Time Out" 
          items={['Team introduction', 'Procedure confirmed', 'Antibiotic given', 'VTE prophylaxis']}
          completed={3}
          total={4}
        />
        <ChecklistSection 
          title="Sign Out" 
          items={['Specimens labelled', 'Equipment count', 'Recovery plan', 'Key concerns']}
          completed={0}
          total={4}
        />
      </div>
    </div>

    <div className="grid md:grid-cols-2 gap-4">
      <div className="mock-ui-card">
        <p className="text-sm font-medium text-slate-700 mb-2">Theatre Schedule</p>
        <div className="p-3 bg-sky-50 rounded-lg border border-sky-200">
          <p className="text-sm text-sky-800">Theatre 3 • 10:30 AM</p>
          <p className="text-xs text-sky-600 mt-1">Est. duration: 45-60 minutes</p>
        </div>
      </div>
      <div className="mock-ui-card">
        <p className="text-sm font-medium text-slate-700 mb-2">ERAS Pre-op Status</p>
        <div className="space-y-1 text-sm">
          <span className="flex items-center gap-2 text-emerald-700"><Check className="w-3 h-3" /> Clear fluids 2h ago</span>
          <span className="flex items-center gap-2 text-emerald-700"><Check className="w-3 h-3" /> Carb drink given</span>
          <span className="flex items-center gap-2 text-emerald-700"><Check className="w-3 h-3" /> VTE stockings applied</span>
        </div>
      </div>
    </div>

    <ERASAlignmentBadge 
      alignment="Standardised perioperative protocols"
      benefit="Reduces surgical delays and improves safety compliance"
    />

    <ValueStatement 
      action="SkyDoc automates checklist verification and coordinates theatre scheduling."
      value="Surgical cancellations reduced by 50%"
    />
  </div>
);

// Step 6: Intraoperative Screen
const IntraoperativeScreen = ({ scenario, isDark }) => (
  <div className="space-y-6">
    <WorkspaceHeader 
      title="Intraoperative Documentation"
      badge={{ text: 'Recording', color: 'warning' }}
    />

    <div className="mock-ui-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Scissors className="w-5 h-5 text-sky-600" />
          <p className="font-medium text-slate-800">{scenario.procedure}</p>
        </div>
        <div className="flex items-center gap-1 text-xs text-red-500">
          <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
          Live Recording
        </div>
      </div>
      
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Procedure Timeline</p>
          <div className="space-y-2">
            <TimelineItem time="10:32" event="Anaesthesia induced" status="done" />
            <TimelineItem time="10:38" event="Incision made" status="done" />
            <TimelineItem time="10:45" event="Appendix identified" status="done" />
            <TimelineItem time="10:52" event="Appendix removed" status="active" />
            <TimelineItem time="--:--" event="Closure" status="pending" />
          </div>
        </div>
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">ERAS Intraop Goals</p>
          <div className="space-y-2">
            <GoalItem label="Minimally invasive approach" met />
            <GoalItem label="Goal-directed fluid therapy" met />
            <GoalItem label="Normothermia maintained" met />
            <GoalItem label="Opioid-sparing analgesia" met />
          </div>
        </div>
      </div>
    </div>

    <div className="grid md:grid-cols-3 gap-4">
      <div className="text-center p-4 bg-white rounded-xl border border-slate-200">
        <p className="text-2xl font-bold text-slate-800">18 min</p>
        <p className="text-xs text-slate-500">Operative time</p>
      </div>
      <div className="text-center p-4 bg-white rounded-xl border border-slate-200">
        <p className="text-2xl font-bold text-emerald-600">&lt; 50ml</p>
        <p className="text-xs text-slate-500">Blood loss</p>
      </div>
      <div className="text-center p-4 bg-white rounded-xl border border-slate-200">
        <p className="text-2xl font-bold text-sky-600">36.8°C</p>
        <p className="text-xs text-slate-500">Core temp</p>
      </div>
    </div>

    <ERASAlignmentBadge 
      alignment="Minimally invasive approach & controlled fluid management"
      benefit="Reduces surgical stress and accelerates recovery"
    />

    <ValueStatement 
      action="SkyDoc captures procedure details, timeline, and ERAS compliance in real-time."
      value="Documentation accuracy 100%"
    />
  </div>
);

// Step 7: Postoperative Monitoring Screen
const PostopMonitoringScreen = ({ scenario, isDark }) => (
  <div className="space-y-6">
    <WorkspaceHeader 
      title="Postoperative Monitoring"
      badge={{ text: 'Recovery', color: 'info' }}
    />

    <div className="grid md:grid-cols-2 gap-6">
      <div className="mock-ui-card">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-medium text-slate-700">Vital Signs Monitoring</p>
          <span className="text-xs text-emerald-600 font-medium">All Normal</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <VitalCard label="Heart Rate" value="72" unit="bpm" status="normal" />
          <VitalCard label="BP" value="124/78" unit="mmHg" status="normal" />
          <VitalCard label="SpO2" value="98" unit="%" status="normal" />
          <VitalCard label="Temp" value="36.9" unit="°C" status="normal" />
        </div>
      </div>

      <div className="mock-ui-card">
        <div className="flex items-center gap-2 mb-3">
          <AlertCircle className="w-5 h-5 text-amber-500" />
          <p className="text-sm font-medium text-slate-700">Early Warning System</p>
        </div>
        <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200 mb-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-emerald-800">NEWS2 Score</span>
            <span className="text-lg font-bold text-emerald-600">1</span>
          </div>
          <p className="text-xs text-emerald-600 mt-1">Low risk - Standard monitoring</p>
        </div>
        <p className="text-xs text-slate-500">AI continuously monitors for early signs of complications</p>
      </div>
    </div>

    <div className="mock-ui-card">
      <p className="text-sm font-medium text-slate-700 mb-3">Complication Screening</p>
      <div className="grid md:grid-cols-4 gap-2">
        {scenario.redFlags.map((flag, i) => (
          <div key={i} className="flex items-center gap-2 p-2 bg-emerald-50 rounded-lg">
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
            <span className="text-xs text-emerald-700">{flag}: Clear</span>
          </div>
        ))}
      </div>
    </div>

    <ERASAlignmentBadge 
      alignment="Early detection of complications"
      benefit="Enables rapid intervention and prevents escalation"
    />

    <ValueStatement 
      action="SkyDoc provides real-time early warning detection and automated complication screening."
      value="Complication detection time reduced by 60%"
    />
  </div>
);

// Step 8: Early Mobilisation Screen
const EarlyMobilisationScreen = ({ scenario, isDark }) => (
  <div className="space-y-6">
    <WorkspaceHeader 
      title="Early Mobilisation & Recovery"
      badge={{ text: 'ERAS Active', color: 'success' }}
    />

    <div className="grid md:grid-cols-3 gap-4">
      <div className="text-center p-6 bg-emerald-50 rounded-xl border border-emerald-200">
        <Timer className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
        <p className="text-3xl font-bold text-emerald-600">4h</p>
        <p className="text-sm text-emerald-700">Time to first mobilisation</p>
        <span className="inline-block mt-2 px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-xs">Target: &lt;6h</span>
      </div>
      <div className="text-center p-6 bg-sky-50 rounded-xl border border-sky-200">
        <Pill className="w-8 h-8 text-sky-600 mx-auto mb-2" />
        <p className="text-3xl font-bold text-sky-600">2h</p>
        <p className="text-sm text-sky-700">Time to oral intake</p>
        <span className="inline-block mt-2 px-2 py-0.5 bg-sky-100 text-sky-700 rounded text-xs">Target: &lt;4h</span>
      </div>
      <div className="text-center p-6 bg-amber-50 rounded-xl border border-amber-200">
        <Activity className="w-8 h-8 text-amber-600 mx-auto mb-2" />
        <p className="text-3xl font-bold text-amber-600">2/10</p>
        <p className="text-sm text-amber-700">Pain score (well controlled)</p>
        <span className="inline-block mt-2 px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs">Opioid-free</span>
      </div>
    </div>

    <div className="mock-ui-card">
      <p className="text-sm font-medium text-slate-700 mb-3">ERAS Recovery Milestones</p>
      <div className="space-y-2">
        <MilestoneItem label="Patient sitting in chair" time="2h post-op" completed />
        <MilestoneItem label="First oral fluids" time="2h post-op" completed />
        <MilestoneItem label="Walking to bathroom" time="4h post-op" completed />
        <MilestoneItem label="Light diet tolerated" time="6h post-op" completed />
        <MilestoneItem label="Pain controlled with oral analgesia" time="8h post-op" active />
      </div>
    </div>

    <ERASAlignmentBadge 
      alignment="Early mobilisation and early feeding"
      benefit="Reduces hospital stay and accelerates functional recovery"
    />

    <ValueStatement 
      action="SkyDoc tracks recovery milestones and prompts nursing staff for ERAS protocol adherence."
      value="Length of stay reduced by 1.5 days"
    />
  </div>
);

// Step 9: Discharge Planning Screen
const DischargePlanningScreen = ({ scenario, isDark }) => (
  <div className="space-y-6">
    <WorkspaceHeader 
      title="Discharge Planning"
      badge={{ text: 'Ready', color: 'success' }}
    />

    <div className="mock-ui-card bg-gradient-to-r from-emerald-50 to-sky-50 border-emerald-200">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-6 h-6 text-emerald-600" />
          <p className="font-medium text-slate-800">Discharge Readiness Assessment</p>
        </div>
        <span className="px-3 py-1 bg-emerald-500 text-white rounded-full text-sm font-medium">READY</span>
      </div>
      <div className="grid md:grid-cols-4 gap-3">
        <DischargeCheck label="Pain controlled" checked />
        <DischargeCheck label="Tolerating diet" checked />
        <DischargeCheck label="Mobilising independently" checked />
        <DischargeCheck label="No complications" checked />
      </div>
    </div>

    <div className="grid md:grid-cols-2 gap-6">
      <div className="mock-ui-card">
        <p className="text-sm font-medium text-slate-700 mb-3">Auto-generated Discharge Summary</p>
        <div className="space-y-2 text-sm">
          <p><strong>Diagnosis:</strong> {scenario.diagnosis}</p>
          <p><strong>Procedure:</strong> {scenario.procedure}</p>
          <p><strong>LOS:</strong> 1 day (Target: {scenario.erasMetrics.losTarget})</p>
          <p><strong>Complications:</strong> None</p>
        </div>
        <button className="mt-3 px-4 py-2 bg-sky-600 text-white rounded-lg text-sm font-medium w-full">
          Generate Full Summary
        </button>
      </div>

      <div className="mock-ui-card">
        <p className="text-sm font-medium text-slate-700 mb-3">Discharge Medications</p>
        <div className="space-y-2">
          {scenario.medications.map((med, i) => (
            <div key={i} className="flex items-center justify-between p-2 bg-white rounded border border-slate-200">
              <span className="text-sm text-slate-700">{med}</span>
              <Check className="w-4 h-4 text-emerald-500" />
            </div>
          ))}
        </div>
      </div>
    </div>

    <ERASAlignmentBadge 
      alignment="Early discharge with structured transition"
      benefit="Safe discharge pathway with clear follow-up plans"
    />

    <ValueStatement 
      action="SkyDoc auto-generates discharge summaries with medication reconciliation and follow-up scheduling."
      value="Discharge processing time reduced by 70%"
    />
  </div>
);

// Step 10: Post-discharge Follow-up Screen
const PostDischargeScreen = ({ scenario, isDark }) => (
  <div className="space-y-6">
    <WorkspaceHeader 
      title="Post-discharge Follow-up"
      badge={{ text: 'AI Calling', color: 'info' }}
    />

    <div className="mock-ui-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 bg-sky-100 rounded-full flex items-center justify-center">
          <Phone className="w-6 h-6 text-sky-600 animate-pulse" />
        </div>
        <div>
          <p className="font-semibold text-slate-800">AI Follow-up Call - Day 3</p>
          <p className="text-sm text-slate-500">Automated complication screening</p>
        </div>
      </div>
      
      <div className="grid md:grid-cols-2 gap-4">
        <div className="p-3 bg-white rounded-lg border border-slate-200">
          <p className="text-xs text-slate-500 mb-2">AI Voice Agent Script:</p>
          <p className="text-sm text-slate-700 italic">"Hi {scenario.patientName}, this is SkyDoc calling to check on your recovery. How is your pain level today on a scale of 1-10?"</p>
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between p-2 bg-emerald-50 rounded">
            <span className="text-sm text-emerald-700">Pain assessment</span>
            <span className="text-xs text-emerald-600">3/10 - Good</span>
          </div>
          <div className="flex items-center justify-between p-2 bg-emerald-50 rounded">
            <span className="text-sm text-emerald-700">Wound status</span>
            <span className="text-xs text-emerald-600">Healing well</span>
          </div>
          <div className="flex items-center justify-between p-2 bg-emerald-50 rounded">
            <span className="text-sm text-emerald-700">Fever/infection</span>
            <span className="text-xs text-emerald-600">None reported</span>
          </div>
        </div>
      </div>
    </div>

    <div className="grid md:grid-cols-3 gap-4">
      <div className="text-center p-4 bg-white rounded-xl border border-slate-200">
        <p className="text-2xl font-bold text-emerald-600">Day 3</p>
        <p className="text-xs text-slate-500">AI call completed</p>
      </div>
      <div className="text-center p-4 bg-white rounded-xl border border-slate-200">
        <p className="text-2xl font-bold text-sky-600">Day 7</p>
        <p className="text-xs text-slate-500">SMS check scheduled</p>
      </div>
      <div className="text-center p-4 bg-white rounded-xl border border-slate-200">
        <p className="text-2xl font-bold text-slate-600">{scenario.followUp}</p>
        <p className="text-xs text-slate-500">Clinic follow-up</p>
      </div>
    </div>

    <ERASAlignmentBadge 
      alignment="Continuity of care"
      benefit="Early detection of post-discharge complications, prevents readmission"
    />

    <ValueStatement 
      action="SkyDoc AI voice agent conducts post-discharge calls for complication screening and outcome tracking."
      value="Readmission rate reduced by 35%"
    />
  </div>
);

// Step 11: Surgical Outcomes Screen
const SurgicalOutcomesScreen = ({ scenario, isDark }) => (
  <div className="space-y-6">
    <WorkspaceHeader 
      title="Outcomes & Revenue Intelligence"
      badge={{ text: 'ERAS Complete', color: 'success' }}
    />

    {/* Key Metrics */}
    <div className="grid md:grid-cols-4 gap-4">
      <MetricCard 
        label="Length of Stay"
        value="1.2 days"
        trend="-40%"
        trendUp={true}
      />
      <MetricCard 
        label="Complication Rate"
        value={`${scenario.analytics.complicationRate}%`}
        trend="-2.1%"
        trendUp={true}
      />
      <MetricCard 
        label="ERAS Compliance"
        value={`${scenario.analytics.erasCompliance}%`}
        trend="+8%"
        trendUp={true}
      />
      <MetricCard 
        label="Patient Satisfaction"
        value="4.8/5"
        trend="+0.3"
        trendUp={true}
      />
    </div>

    <div className="grid md:grid-cols-2 gap-6">
      {/* Revenue Summary */}
      <div className="mock-ui-card">
        <div className="flex items-center gap-2 mb-4">
          <DollarSign className="w-5 h-5 text-emerald-600" />
          <p className="text-sm font-medium text-slate-700">Revenue Intelligence</p>
        </div>
        <div className="space-y-3">
          <div className="flex items-center justify-between py-2 border-b border-slate-100">
            <span className="text-slate-600">ICD-10 codes captured</span>
            <span className="font-medium text-slate-800">{scenario.icdCodes.length}</span>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-slate-100">
            <span className="text-slate-600">Procedure coding accuracy</span>
            <span className="font-medium text-emerald-600">100%</span>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-slate-100">
            <span className="text-slate-600">Documentation completeness</span>
            <span className="font-medium text-emerald-600">100%</span>
          </div>
          <div className="flex items-center justify-between py-2">
            <span className="text-slate-600 font-medium">Claim status</span>
            <span className="mock-ui-badge success">Submitted</span>
          </div>
        </div>
      </div>

      {/* ERAS Benchmarking */}
      <div className="mock-ui-card">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 className="w-5 h-5 text-sky-600" />
          <p className="text-sm font-medium text-slate-700">ERAS Benchmarking</p>
        </div>
        <div className="space-y-3">
          <BenchmarkBar label="LOS vs Target" actual={1.2} target={2} unit="days" better="lower" />
          <BenchmarkBar label="Mobilisation Time" actual={4} target={6} unit="hours" better="lower" />
          <BenchmarkBar label="Oral Intake Time" actual={2} target={4} unit="hours" better="lower" />
          <BenchmarkBar label="Complication Rate" actual={3.2} target={5} unit="%" better="lower" />
        </div>
      </div>
    </div>

    <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
      <div className="flex items-start gap-3">
        <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0" />
        <div>
          <p className="font-medium text-emerald-800">ERAS Pathway Complete</p>
          <p className="text-sm text-emerald-700">
            This surgical episode achieved 94% ERAS compliance with outcomes exceeding benchmark targets.
            Length of stay was 40% below the national average for this procedure.
          </p>
        </div>
      </div>
    </div>

    <ValueStatement 
      action="SkyDoc provides comprehensive surgical outcomes tracking with ERAS benchmarking and revenue intelligence."
      value="Complete visibility from referral to recovery"
      highlight
    />
  </div>
);

// Helper Components for Surgical Screens
const ERASAlignmentBadge = ({ alignment, benefit }) => (
  <div className="p-3 bg-sky-50 border border-sky-200 rounded-xl">
    <div className="flex items-start gap-2">
      <Shield className="w-4 h-4 text-sky-600 mt-0.5 flex-shrink-0" />
      <div>
        <p className="text-xs text-sky-600 font-medium uppercase tracking-wide">ERAS Alignment</p>
        <p className="text-sm text-sky-800 font-medium">{alignment}</p>
        <p className="text-xs text-sky-600 mt-0.5">{benefit}</p>
      </div>
    </div>
  </div>
);

const RiskScoreBadge = ({ label, value, color }) => (
  <div className={`p-3 rounded-lg bg-${color}-50 border border-${color}-200`}>
    <p className="text-xs text-slate-500">{label}</p>
    <p className={`text-lg font-bold text-${color}-600`}>{value}</p>
  </div>
);

const PrehbCard = ({ icon: Icon, title, status, items, color }) => (
  <div className="mock-ui-card">
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <Icon className={`w-5 h-5 text-${color}-500`} />
        <span className="text-sm font-medium text-slate-700">{title}</span>
      </div>
      <span className={`text-xs px-2 py-0.5 rounded ${
        status === 'Optimised' ? 'bg-emerald-100 text-emerald-700' :
        status === 'In Progress' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'
      }`}>{status}</span>
    </div>
    <ul className="space-y-1 text-xs text-slate-600">
      {items.map((item, i) => <li key={i}>• {item}</li>)}
    </ul>
  </div>
);

const EducationItem = ({ title, desc, sent, pending }) => (
  <div className="flex items-center justify-between p-2 bg-white rounded border border-slate-200">
    <div>
      <p className="text-sm font-medium text-slate-700">{title}</p>
      <p className="text-xs text-slate-500">{desc}</p>
    </div>
    {sent && <span className="mock-ui-badge success">Sent</span>}
    {pending && <span className="mock-ui-badge warning">Pending</span>}
  </div>
);

const ChecklistSection = ({ title, items, completed, total }) => (
  <div className="p-3 bg-white rounded-lg border border-slate-200">
    <div className="flex items-center justify-between mb-2">
      <span className="text-sm font-medium text-slate-700">{title}</span>
      <span className={`text-xs ${completed === total ? 'text-emerald-600' : 'text-slate-500'}`}>
        {completed}/{total}
      </span>
    </div>
    <div className="space-y-1">
      {items.map((item, i) => (
        <div key={i} className="flex items-center gap-2 text-xs">
          {i < completed ? (
            <CheckCircle2 className="w-3 h-3 text-emerald-500" />
          ) : (
            <div className="w-3 h-3 border border-slate-300 rounded-full" />
          )}
          <span className={i < completed ? 'text-slate-700' : 'text-slate-400'}>{item}</span>
        </div>
      ))}
    </div>
  </div>
);

const TimelineItem = ({ time, event, status }) => (
  <div className="flex items-center gap-3">
    <span className="text-xs font-mono text-slate-400 w-12">{time}</span>
    <div className={`w-2 h-2 rounded-full ${
      status === 'done' ? 'bg-emerald-500' : status === 'active' ? 'bg-sky-500 animate-pulse' : 'bg-slate-300'
    }`} />
    <span className={`text-sm ${status === 'done' ? 'text-slate-700' : status === 'active' ? 'text-sky-600 font-medium' : 'text-slate-400'}`}>
      {event}
    </span>
  </div>
);

const GoalItem = ({ label, met }) => (
  <div className="flex items-center gap-2">
    {met ? (
      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
    ) : (
      <div className="w-4 h-4 border border-slate-300 rounded-full" />
    )}
    <span className={`text-sm ${met ? 'text-slate-700' : 'text-slate-400'}`}>{label}</span>
  </div>
);

const VitalCard = ({ label, value, unit, status }) => (
  <div className="p-3 bg-white rounded-lg border border-slate-200 text-center">
    <p className="text-xs text-slate-500">{label}</p>
    <p className="text-xl font-bold text-slate-800">{value}<span className="text-xs text-slate-400 ml-0.5">{unit}</span></p>
    <div className={`w-2 h-2 rounded-full mx-auto mt-1 ${status === 'normal' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
  </div>
);

const MilestoneItem = ({ label, time, completed, active }) => (
  <div className={`flex items-center justify-between p-2 rounded-lg ${
    completed ? 'bg-emerald-50' : active ? 'bg-sky-50' : 'bg-slate-50'
  }`}>
    <div className="flex items-center gap-2">
      {completed ? (
        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
      ) : active ? (
        <div className="w-4 h-4 border-2 border-sky-500 rounded-full animate-pulse" />
      ) : (
        <div className="w-4 h-4 border border-slate-300 rounded-full" />
      )}
      <span className={`text-sm ${completed ? 'text-emerald-700' : active ? 'text-sky-700' : 'text-slate-600'}`}>{label}</span>
    </div>
    <span className="text-xs text-slate-500">{time}</span>
  </div>
);

const DischargeCheck = ({ label, checked }) => (
  <div className="flex items-center gap-2">
    <CheckCircle2 className={`w-4 h-4 ${checked ? 'text-emerald-500' : 'text-slate-300'}`} />
    <span className="text-sm text-slate-700">{label}</span>
  </div>
);

const BenchmarkBar = ({ label, actual, target, unit, better }) => {
  const performance = better === 'lower' ? (target - actual) / target * 100 : (actual - target) / target * 100;
  const isGood = better === 'lower' ? actual <= target : actual >= target;
  
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-slate-600">{label}</span>
        <span className={isGood ? 'text-emerald-600' : 'text-amber-600'}>
          {actual}{unit} vs {target}{unit}
        </span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div 
          className={`h-full rounded-full ${isGood ? 'bg-emerald-500' : 'bg-amber-500'}`}
          style={{ width: `${Math.min(100, (actual / target) * 100)}%` }}
        />
      </div>
    </div>
  );
};

// ===== RETINA DR/DME SCREENS =====

const RetinaScanScreen = ({ scenario, isDark }) => (
  <div className="p-5 space-y-4" data-testid="retina-scan-screen">
    <div className={`p-4 rounded-xl ${isDark ? 'bg-slate-800' : 'bg-violet-50'}`}>
      <div className="flex items-center gap-2 mb-3">
        <Eye className={`w-5 h-5 ${isDark ? 'text-violet-400' : 'text-violet-600'}`} />
        <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>Fundus & OCT Capture</span>
      </div>
      <div className={`text-xs ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
        <p><strong>Patient:</strong> {scenario.patientName}, {scenario.age}y</p>
        <p className="mt-1"><strong>Presenting:</strong> {scenario.symptoms}</p>
      </div>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <div className={`p-3 rounded-xl border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
        <p className={`text-xs font-bold mb-2 ${isDark ? 'text-violet-400' : 'text-violet-600'}`}>Fundus Image</p>
        <div className={`h-24 rounded-lg flex items-center justify-center ${isDark ? 'bg-slate-700' : 'bg-slate-100'}`}>
          <Eye className={`w-8 h-8 ${isDark ? 'text-slate-500' : 'text-slate-300'}`} />
        </div>
        <p className={`text-xs mt-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Right eye, 45° field</p>
      </div>
      <div className={`p-3 rounded-xl border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
        <p className={`text-xs font-bold mb-2 ${isDark ? 'text-violet-400' : 'text-violet-600'}`}>OCT Scan</p>
        <div className={`h-24 rounded-lg flex items-center justify-center ${isDark ? 'bg-slate-700' : 'bg-slate-100'}`}>
          <Scan className={`w-8 h-8 ${isDark ? 'text-slate-500' : 'text-slate-300'}`} />
        </div>
        <p className={`text-xs mt-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Macular cube 6x6mm</p>
      </div>
    </div>
    <div className={`p-3 rounded-lg ${isDark ? 'bg-emerald-900/20 border border-emerald-800' : 'bg-emerald-50 border border-emerald-200'}`}>
      <p className={`text-xs ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>
        <strong>Status:</strong> Images captured and securely uploaded to SkyDoc for AI analysis
      </p>
    </div>
  </div>
);

const RetinaSegmentationScreen = ({ scenario, isDark }) => (
  <div className="p-5 space-y-4" data-testid="retina-segmentation-screen">
    <div className={`p-4 rounded-xl ${isDark ? 'bg-slate-800' : 'bg-violet-50'}`}>
      <div className="flex items-center gap-2 mb-2">
        <Scan className={`w-5 h-5 ${isDark ? 'text-violet-400' : 'text-violet-600'}`} />
        <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>AI Retinal Segmentation</span>
      </div>
      <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
        SkyDoc AI segments retinal layers, identifies fluid pockets, and maps structural abnormalities
      </p>
    </div>
    <div className="space-y-2">
      {['Inner Limiting Membrane (ILM)', 'Nerve Fiber Layer (NFL)', 'Ganglion Cell Layer (GCL)', 'Inner Nuclear Layer (INL)', 'Outer Plexiform Layer (OPL)', 'Retinal Pigment Epithelium (RPE)'].map((layer, i) => (
        <div key={i} className={`flex items-center justify-between p-2 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-white border border-slate-100'}`}>
          <span className={`text-xs ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{layer}</span>
          <span className={`text-xs font-medium ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>Segmented</span>
        </div>
      ))}
    </div>
    <div className={`p-3 rounded-lg ${isDark ? 'bg-sky-900/20 border border-sky-800' : 'bg-sky-50 border border-sky-200'}`}>
      <p className={`text-xs ${isDark ? 'text-sky-400' : 'text-sky-700'}`}>
        <strong>Finding:</strong> Intraretinal cystic changes detected in INL/OPL — consistent with CI-DME
      </p>
    </div>
  </div>
);

const RetinaBiomarkerScreen = ({ scenario, isDark }) => (
  <div className="p-5 space-y-4" data-testid="retina-biomarker-screen">
    <div className={`p-4 rounded-xl ${isDark ? 'bg-slate-800' : 'bg-violet-50'}`}>
      <div className="flex items-center gap-2 mb-2">
        <Target className={`w-5 h-5 ${isDark ? 'text-violet-400' : 'text-violet-600'}`} />
        <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>Biomarker Detection & Scoring</span>
      </div>
    </div>
    <div className="grid grid-cols-2 gap-2">
      {Object.entries(scenario.retinaBiomarkers || {}).map(([key, value]) => {
        const labels = {
          intraretinalFluid: 'Intraretinal Fluid',
          subretinalFluid: 'Subretinal Fluid',
          crt: 'Central Retinal Thickness',
          drilScore: 'DRIL Score',
          hrf: 'Hyperreflective Foci',
          fluidVolume: '3D Fluid Volume',
        };
        return (
          <div key={key} className={`p-3 rounded-xl border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
            <p className={`text-xs mb-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{labels[key] || key}</p>
            <p className={`text-xs font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>{value}</p>
          </div>
        );
      })}
    </div>
  </div>
);

const RetinaFluidScreen = ({ scenario, isDark }) => (
  <div className="p-5 space-y-4" data-testid="retina-fluid-screen">
    <div className={`p-4 rounded-xl ${isDark ? 'bg-slate-800' : 'bg-violet-50'}`}>
      <div className="flex items-center gap-2 mb-2">
        <Layers className={`w-5 h-5 ${isDark ? 'text-violet-400' : 'text-violet-600'}`} />
        <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>Voxel-Level Fluid Quantification</span>
      </div>
      <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
        AI quantifies microscopic fluid volume in 3D across retinal layers — beyond standard thickness measurements
      </p>
    </div>
    <div className={`p-4 rounded-xl border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Total Fluid Volume</span>
          <span className={`text-sm font-bold ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>{scenario.retinaBiomarkers?.fluidVolume || '0.42 mm³'}</span>
        </div>
        <div className={`h-3 rounded-full overflow-hidden ${isDark ? 'bg-slate-700' : 'bg-slate-100'}`}>
          <div className="h-full bg-amber-500 rounded-full" style={{ width: '62%' }} />
        </div>
        <div className="flex justify-between text-xs">
          <span className={isDark ? 'text-slate-500' : 'text-slate-400'}>Normal: {'<'}0.1 mm³</span>
          <span className={isDark ? 'text-amber-400' : 'text-amber-600'}>Elevated</span>
        </div>
      </div>
    </div>
    <div className="grid grid-cols-3 gap-2">
      {[
        { label: 'Intraretinal', val: '0.31 mm³', pct: 74 },
        { label: 'Subretinal', val: '0.08 mm³', pct: 19 },
        { label: 'Sub-RPE', val: '0.03 mm³', pct: 7 },
      ].map((f, i) => (
        <div key={i} className={`p-3 rounded-xl text-center ${isDark ? 'bg-slate-800' : 'bg-slate-50'}`}>
          <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{f.label}</p>
          <p className={`text-sm font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>{f.val}</p>
          <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{f.pct}%</p>
        </div>
      ))}
    </div>
  </div>
);

const RetinaLongitudinalScreen = ({ scenario, isDark }) => (
  <div className="p-5 space-y-4" data-testid="retina-longitudinal-screen">
    <div className={`p-4 rounded-xl ${isDark ? 'bg-slate-800' : 'bg-violet-50'}`}>
      <div className="flex items-center gap-2 mb-2">
        <TrendingUp className={`w-5 h-5 ${isDark ? 'text-violet-400' : 'text-violet-600'}`} />
        <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>Longitudinal Comparison</span>
      </div>
      <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>AI aligns scans across visits to detect changes in swelling and disease activity</p>
    </div>
    <div className={`p-4 rounded-xl border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
      <p className={`text-xs font-bold mb-3 ${isDark ? 'text-white' : 'text-slate-800'}`}>Central Retinal Thickness (CRT) Trend</p>
      <div className="flex items-end gap-3 h-24">
        {(scenario.treatmentResponse?.visitsTrend || ['412','398','392','385']).map((val, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-1">
            <span className={`text-xs font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>{val}</span>
            <div className={`w-full rounded-t-md ${i === 3 ? 'bg-sky-500' : isDark ? 'bg-slate-600' : 'bg-slate-300'}`} style={{ height: `${(parseInt(val) / 500) * 100}%` }} />
            <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>V{i + 1}</span>
          </div>
        ))}
      </div>
      <div className={`mt-3 flex items-center justify-between text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
        <span>Target: {scenario.treatmentResponse?.dryWeightTarget || '280 µm'}</span>
        <span className="text-sky-500 font-medium">-6.6% over 4 visits</span>
      </div>
    </div>
    <div className={`p-3 rounded-lg ${isDark ? 'bg-amber-900/20 border border-amber-800' : 'bg-amber-50 border border-amber-200'}`}>
      <p className={`text-xs ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>
        <strong>Assessment:</strong> CRT reducing but above target — classified as slow responder
      </p>
    </div>
  </div>
);

const RetinaRiskScreen = ({ scenario, isDark }) => (
  <div className="p-5 space-y-4" data-testid="retina-risk-screen">
    <div className={`p-4 rounded-xl ${isDark ? 'bg-slate-800' : 'bg-violet-50'}`}>
      <div className="flex items-center gap-2 mb-2">
        <AlertTriangle className={`w-5 h-5 ${isDark ? 'text-violet-400' : 'text-violet-600'}`} />
        <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>Risk Stratification</span>
      </div>
    </div>
    <div className="space-y-2">
      {[
        { label: 'Progression Risk', level: 'Moderate', color: 'amber' },
        { label: 'PDR Conversion Risk', level: 'Low-Moderate', color: 'amber' },
        { label: 'Treatment Non-response', level: 'High', color: 'rose' },
        { label: 'Vision Loss (2yr)', level: 'Moderate', color: 'amber' },
        { label: 'Adherence Dropout', level: 'Moderate', color: 'amber' },
      ].map((r, i) => (
        <div key={i} className={`flex items-center justify-between p-3 rounded-xl ${isDark ? 'bg-slate-800' : 'bg-white border border-slate-100'}`}>
          <span className={`text-xs ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{r.label}</span>
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
            r.color === 'rose' 
              ? isDark ? 'bg-rose-900/40 text-rose-400' : 'bg-rose-100 text-rose-700'
              : isDark ? 'bg-amber-900/40 text-amber-400' : 'bg-amber-100 text-amber-700'
          }`}>{r.level}</span>
        </div>
      ))}
    </div>
    <div className={`p-3 rounded-lg ${isDark ? 'bg-rose-900/20 border border-rose-800' : 'bg-rose-50 border border-rose-200'}`}>
      <p className={`text-xs ${isDark ? 'text-rose-400' : 'text-rose-700'}`}>
        <strong>Red Flags:</strong> {(scenario.redFlags || []).join(' · ')}
      </p>
    </div>
  </div>
);

const RetinaTreatmentScreen = ({ scenario, isDark }) => (
  <div className="p-5 space-y-4" data-testid="retina-treatment-screen">
    <div className={`p-4 rounded-xl ${isDark ? 'bg-slate-800' : 'bg-violet-50'}`}>
      <div className="flex items-center gap-2 mb-2">
        <Brain className={`w-5 h-5 ${isDark ? 'text-violet-400' : 'text-violet-600'}`} />
        <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>Treatment Recommendation</span>
      </div>
    </div>
    <div className={`p-4 rounded-xl border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
      <p className={`text-xs font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>AI Treatment Guidance</p>
      <div className="space-y-2">
        <div className={`p-2 rounded-lg ${isDark ? 'bg-sky-900/20' : 'bg-sky-50'}`}>
          <p className={`text-xs ${isDark ? 'text-sky-400' : 'text-sky-700'}`}>
            <strong>Response:</strong> {scenario.treatmentResponse?.velocity || 'Slow responder'}
          </p>
        </div>
        <div className={`p-2 rounded-lg ${isDark ? 'bg-amber-900/20' : 'bg-amber-50'}`}>
          <p className={`text-xs ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>
            <strong>Action:</strong> {scenario.treatmentResponse?.nextAction || 'Consider switching anti-VEGF agent'}
          </p>
        </div>
      </div>
    </div>
    <div className={`p-3 rounded-xl border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
      <p className={`text-xs font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>Current Medications</p>
      <div className="space-y-1">
        {(scenario.medications || []).map((med, i) => (
          <div key={i} className={`text-xs p-1.5 rounded ${isDark ? 'bg-slate-700 text-slate-300' : 'bg-slate-50 text-slate-600'}`}>{med}</div>
        ))}
      </div>
    </div>
    <div className={`p-3 rounded-xl border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
      <p className={`text-xs font-bold mb-1 ${isDark ? 'text-white' : 'text-slate-800'}`}>Guidelines</p>
      <div className="flex flex-wrap gap-1">
        {(scenario.guidelines || []).map((g, i) => (
          <span key={i} className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-violet-900/30 text-violet-400' : 'bg-violet-50 text-violet-700'}`}>{g}</span>
        ))}
      </div>
    </div>
  </div>
);

const RetinaProgressScreen = ({ scenario, isDark }) => (
  <div className="p-5 space-y-4" data-testid="retina-progress-screen">
    <div className={`p-4 rounded-xl ${isDark ? 'bg-slate-800' : 'bg-violet-50'}`}>
      <div className="flex items-center gap-2 mb-2">
        <UserCheck className={`w-5 h-5 ${isDark ? 'text-violet-400' : 'text-violet-600'}`} />
        <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>Patient Progress Report</span>
      </div>
      <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Visual progress report generated for patient communication</p>
    </div>
    <div className={`p-4 rounded-xl border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
      <p className={`text-xs font-bold mb-3 ${isDark ? 'text-white' : 'text-slate-800'}`}>Recovery Timeline — {scenario.patientName}</p>
      <div className="space-y-2">
        {[
          { visit: 'Baseline', crt: '412 µm', status: 'Initial assessment' },
          { visit: 'Visit 2 (+4wk)', crt: '398 µm', status: 'Mild improvement' },
          { visit: 'Visit 3 (+8wk)', crt: '392 µm', status: 'Slow response' },
          { visit: 'Visit 4 (+12wk)', crt: '385 µm', status: 'Below expected trajectory' },
        ].map((v, i) => (
          <div key={i} className={`flex items-center justify-between p-2 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-slate-50'}`}>
            <span className={`text-xs font-medium ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>{v.visit}</span>
            <span className={`text-xs font-bold ${isDark ? 'text-sky-400' : 'text-sky-600'}`}>{v.crt}</span>
            <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{v.status}</span>
          </div>
        ))}
      </div>
    </div>
    <div className={`p-3 rounded-lg ${isDark ? 'bg-sky-900/20 border border-sky-800' : 'bg-sky-50 border border-sky-200'}`}>
      <p className={`text-xs ${isDark ? 'text-sky-400' : 'text-sky-700'}`}>
        <strong>Patient note:</strong> Your retinal swelling has reduced by 6.6% over 12 weeks. While progress is slower than expected, treatment is working. Your doctor may recommend adjusting your treatment plan.
      </p>
    </div>
  </div>
);

const RetinaAdherenceScreen = ({ scenario, isDark }) => (
  <div className="p-5 space-y-4" data-testid="retina-adherence-screen">
    <div className={`p-4 rounded-xl ${isDark ? 'bg-slate-800' : 'bg-violet-50'}`}>
      <div className="flex items-center gap-2 mb-2">
        <Navigation2 className={`w-5 h-5 ${isDark ? 'text-violet-400' : 'text-violet-600'}`} />
        <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>Follow-up & Adherence</span>
      </div>
    </div>
    <div className={`p-4 rounded-xl border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
      <p className={`text-xs font-bold mb-3 ${isDark ? 'text-white' : 'text-slate-800'}`}>Next Steps</p>
      <div className="space-y-2">
        {[
          { action: 'Follow-up injection scheduled', when: '4 weeks', status: 'Booked' },
          { action: 'OCT scan on next visit', when: 'Pre-injection', status: 'Pending' },
          { action: 'HbA1c recheck', when: '3 months', status: 'Ordered' },
          { action: 'BP review with internist', when: '2 weeks', status: 'Referred' },
        ].map((a, i) => (
          <div key={i} className={`flex items-center justify-between p-2 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-slate-50'}`}>
            <span className={`text-xs ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>{a.action}</span>
            <div className="flex items-center gap-2">
              <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{a.when}</span>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                a.status === 'Booked' ? isDark ? 'bg-emerald-900/40 text-emerald-400' : 'bg-emerald-100 text-emerald-700'
                : isDark ? 'bg-sky-900/40 text-sky-400' : 'bg-sky-100 text-sky-700'
              }`}>{a.status}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
    <div className={`p-4 rounded-xl border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
      <p className={`text-xs font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>Adherence Monitoring</p>
      <div className="flex items-center gap-3">
        <div className={`flex-1 h-2 rounded-full ${isDark ? 'bg-slate-700' : 'bg-slate-100'}`}>
          <div className="h-full bg-emerald-500 rounded-full" style={{ width: '78%' }} />
        </div>
        <span className={`text-xs font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>78%</span>
      </div>
      <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Treatment compliance over last 6 months</p>
    </div>
  </div>
);

const RetinaAnalyticsScreen = ({ scenario, isDark }) => (
  <div className="p-5 space-y-4" data-testid="retina-analytics-screen">
    <div className={`p-4 rounded-xl ${isDark ? 'bg-slate-800' : 'bg-violet-50'}`}>
      <div className="flex items-center gap-2 mb-2">
        <BarChart3 className={`w-5 h-5 ${isDark ? 'text-violet-400' : 'text-violet-600'}`} />
        <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>Retina Analytics Dashboard</span>
      </div>
    </div>
    <div className="grid grid-cols-2 gap-3">
      {[
        { label: 'Patients Monitored', value: scenario.analytics?.patientsMonitored || 342 },
        { label: 'Avg Injection Interval', value: `${scenario.analytics?.avgInjectionInterval || '5.2'} wks` },
        { label: 'Treatment Compliance', value: `${scenario.analytics?.treatmentCompliance || 78}%` },
        { label: 'Vision Stabilized', value: `${scenario.analytics?.visionStabilized || 89}%` },
      ].map((m, i) => (
        <div key={i} className={`p-3 rounded-xl text-center ${isDark ? 'bg-slate-800' : 'bg-white border border-slate-200'}`}>
          <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{m.label}</p>
          <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>{m.value}</p>
        </div>
      ))}
    </div>
    <div className={`p-4 rounded-xl border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
      <p className={`text-xs font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>Population Insights</p>
      <div className="space-y-2">
        {[
          { label: 'Fast responders', pct: 45, color: 'bg-emerald-500' },
          { label: 'Slow responders', pct: 32, color: 'bg-amber-500' },
          { label: 'Non-responders', pct: 12, color: 'bg-rose-500' },
          { label: 'Recurrence detected', pct: 11, color: 'bg-violet-500' },
        ].map((p, i) => (
          <div key={i}>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className={isDark ? 'text-slate-300' : 'text-slate-600'}>{p.label}</span>
              <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>{p.pct}%</span>
            </div>
            <div className={`h-1.5 rounded-full ${isDark ? 'bg-slate-700' : 'bg-slate-100'}`}>
              <div className={`h-full rounded-full ${p.color}`} style={{ width: `${p.pct}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ===== IVF CENTRE SCREENS =====

const IVFCoupleScreen = ({ scenario, isDark }) => (
  <div className="p-5 space-y-4" data-testid="ivf-couple-screen">
    <div className={`p-4 rounded-xl ${isDark ? 'bg-slate-800' : 'bg-rose-50'}`}>
      <div className="flex items-center gap-2 mb-2">
        <Users className={`w-5 h-5 ${isDark ? 'text-rose-400' : 'text-rose-600'}`} />
        <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>Couple Assessment & History</span>
      </div>
      <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>AI Scribe captures conversation and structures full couple profile</p>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <div className={`p-3 rounded-xl border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
        <p className={`text-xs font-bold mb-2 ${isDark ? 'text-rose-400' : 'text-rose-600'}`}>Female Profile</p>
        {Object.entries(scenario.ivfData?.coupleProfile?.female || {}).map(([k, v]) => (
          <div key={k} className="flex justify-between text-xs py-0.5">
            <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>{k.toUpperCase()}</span>
            <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>{v}</span>
          </div>
        ))}
      </div>
      <div className={`p-3 rounded-xl border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
        <p className={`text-xs font-bold mb-2 ${isDark ? 'text-sky-400' : 'text-sky-600'}`}>Male Profile</p>
        {Object.entries(scenario.ivfData?.coupleProfile?.male || {}).map(([k, v]) => (
          <div key={k} className="flex justify-between text-xs py-0.5">
            <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>{k.toUpperCase()}</span>
            <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>{v}</span>
          </div>
        ))}
      </div>
    </div>
    <div className={`p-3 rounded-lg ${isDark ? 'bg-amber-900/20 border border-amber-800' : 'bg-amber-50 border border-amber-200'}`}>
      <p className={`text-xs ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>
        <strong>Risk Flag:</strong> PCOS with anovulation · 2 failed IUI cycles · Mild male factor (low morphology)
      </p>
    </div>
  </div>
);

const IVFInvestigationsScreen = ({ scenario, isDark }) => (
  <div className="p-5 space-y-4" data-testid="ivf-investigations-screen">
    <div className={`p-4 rounded-xl ${isDark ? 'bg-slate-800' : 'bg-rose-50'}`}>
      <div className="flex items-center gap-2 mb-2">
        <FlaskConical className={`w-5 h-5 ${isDark ? 'text-rose-400' : 'text-rose-600'}`} />
        <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>Investigations & Labs</span>
      </div>
      <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Investigation Agent auto-creates checklists and flags missing tests</p>
    </div>
    <div className="grid grid-cols-2 gap-2">
      {[
        { test: 'AMH', val: '4.8 ng/mL', flag: 'High (OHSS risk)' },
        { test: 'FSH (Day 2)', val: '5.84 mIU/mL', flag: 'Normal' },
        { test: 'LH (Day 2)', val: '8.2 mIU/mL', flag: 'Elevated (PCOS)' },
        { test: 'TSH', val: '2.99 µIU/mL', flag: 'Normal' },
        { test: 'AFC', val: '18 follicles', flag: 'Good reserve' },
        { test: 'HSG', val: 'Bilateral patent', flag: 'Normal' },
        { test: 'Semen Count', val: '32 M/mL', flag: 'Borderline' },
        { test: 'Sperm Morphology', val: '4%', flag: 'Low' },
      ].map((t, i) => (
        <div key={i} className={`p-2 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-white border border-slate-100'}`}>
          <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{t.test}</p>
          <p className={`text-xs font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>{t.val}</p>
          <p className={`text-xs ${t.flag.includes('Low') || t.flag.includes('High') || t.flag.includes('Elevated') ? (isDark ? 'text-amber-400' : 'text-amber-600') : (isDark ? 'text-emerald-400' : 'text-emerald-600')}`}>{t.flag}</p>
        </div>
      ))}
    </div>
  </div>
);

const IVFDiagnosisScreen = ({ scenario, isDark }) => (
  <div className="p-5 space-y-4" data-testid="ivf-diagnosis-screen">
    <div className={`p-4 rounded-xl ${isDark ? 'bg-slate-800' : 'bg-rose-50'}`}>
      <div className="flex items-center gap-2 mb-2">
        <Brain className={`w-5 h-5 ${isDark ? 'text-rose-400' : 'text-rose-600'}`} />
        <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>Diagnosis & Treatment Decision</span>
      </div>
    </div>
    <div className={`p-4 rounded-xl border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
      <p className={`text-xs font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>AI Decision Agent Recommendation</p>
      <p className={`text-xs mb-3 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{scenario.diagnosis}</p>
      <div className="space-y-2">
        {[
          { option: 'IVF + ICSI (Recommended)', reason: 'Best option given 2 failed IUI + mild male factor', selected: true },
          { option: 'IUI (3rd attempt)', reason: 'Lower success probability after 2 failures', selected: false },
          { option: 'Freeze All', reason: 'Consider if OHSS risk materializes during stim', selected: false },
        ].map((opt, i) => (
          <div key={i} className={`p-2 rounded-lg ${opt.selected ? (isDark ? 'bg-rose-900/30 border border-rose-700' : 'bg-rose-50 border border-rose-200') : (isDark ? 'bg-slate-700' : 'bg-slate-50')}`}>
            <div className="flex items-center gap-2">
              {opt.selected && <Check className="w-3.5 h-3.5 text-rose-500" />}
              <span className={`text-xs font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>{opt.option}</span>
            </div>
            <p className={`text-xs mt-0.5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{opt.reason}</p>
          </div>
        ))}
      </div>
    </div>
    <div className="flex flex-wrap gap-1">
      {(scenario.guidelines || []).map((g, i) => (
        <span key={i} className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-rose-900/30 text-rose-400' : 'bg-rose-50 text-rose-700'}`}>{g}</span>
      ))}
    </div>
  </div>
);

const IVFStimulationScreen = ({ scenario, isDark }) => (
  <div className="p-5 space-y-4" data-testid="ivf-stimulation-screen">
    <div className={`p-4 rounded-xl ${isDark ? 'bg-slate-800' : 'bg-rose-50'}`}>
      <div className="flex items-center gap-2 mb-2">
        <Pill className={`w-5 h-5 ${isDark ? 'text-rose-400' : 'text-rose-600'}`} />
        <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>Ovarian Stimulation Protocol</span>
      </div>
      <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Medication Agent manages drugs, schedules, compliance, and injection tracking</p>
    </div>
    <div className={`p-4 rounded-xl border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
      {[
        { label: 'Protocol', value: scenario.ivfData?.stimulation?.protocol || 'Antagonist' },
        { label: 'Starting Dose', value: scenario.ivfData?.stimulation?.startDose || 'Gonal-F 150 IU' },
        { label: 'Stimulation Days', value: scenario.ivfData?.stimulation?.stimDays || 10 },
        { label: 'Antagonist Start', value: scenario.ivfData?.stimulation?.antagonistStart || 'Day 6' },
      ].map((r, i) => (
        <div key={i} className="flex justify-between py-1.5 border-b last:border-0 border-slate-100 dark:border-slate-700">
          <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{r.label}</span>
          <span className={`text-xs font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>{r.value}</span>
        </div>
      ))}
    </div>
    <div className={`p-3 rounded-xl border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
      <p className={`text-xs font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>Daily Injection Schedule</p>
      <div className="grid grid-cols-3 gap-2 text-center">
        {[{ time: 'Morning', drug: 'Gonal-F 150 IU' }, { time: 'Afternoon', drug: '—' }, { time: 'Night', drug: 'Cetrotide 0.25mg' }].map((inj, i) => (
          <div key={i} className={`p-2 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-slate-50'}`}>
            <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{inj.time}</p>
            <p className={`text-xs font-medium ${isDark ? 'text-white' : 'text-slate-700'}`}>{inj.drug}</p>
          </div>
        ))}
      </div>
    </div>
    <div className={`p-3 rounded-lg ${isDark ? 'bg-amber-900/20 border border-amber-800' : 'bg-amber-50 border border-amber-200'}`}>
      <p className={`text-xs ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>
        <strong>AI Risk Monitor:</strong> AMH 4.8 — moderate OHSS risk. Dose capped at 150 IU. Close monitoring from Day 6.
      </p>
    </div>
  </div>
);

const IVFMonitoringScreen = ({ scenario, isDark }) => (
  <div className="p-5 space-y-4" data-testid="ivf-monitoring-screen">
    <div className={`p-4 rounded-xl ${isDark ? 'bg-slate-800' : 'bg-rose-50'}`}>
      <div className="flex items-center gap-2 mb-2">
        <Scan className={`w-5 h-5 ${isDark ? 'text-rose-400' : 'text-rose-600'}`} />
        <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>Follicular Monitoring</span>
      </div>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <div className={`p-3 rounded-xl border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
        <p className={`text-xs font-bold mb-2 ${isDark ? 'text-rose-400' : 'text-rose-600'}`}>Right Ovary</p>
        <div className="space-y-1">
          {(scenario.ivfData?.monitoring?.follicles?.right || []).map((f, i) => (
            <div key={i} className={`flex items-center justify-between text-xs p-1 rounded ${i === 0 ? (isDark ? 'bg-rose-900/20' : 'bg-rose-50') : ''}`}>
              <span className={isDark ? 'text-slate-300' : 'text-slate-600'}>F{i + 1}</span>
              <span className={`font-bold ${i === 0 ? 'text-rose-500' : (isDark ? 'text-white' : 'text-slate-800')}`}>{f}</span>
            </div>
          ))}
        </div>
      </div>
      <div className={`p-3 rounded-xl border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
        <p className={`text-xs font-bold mb-2 ${isDark ? 'text-sky-400' : 'text-sky-600'}`}>Left Ovary</p>
        <div className="space-y-1">
          {(scenario.ivfData?.monitoring?.follicles?.left || []).map((f, i) => (
            <div key={i} className={`flex items-center justify-between text-xs p-1 rounded ${i === 0 ? (isDark ? 'bg-sky-900/20' : 'bg-sky-50') : ''}`}>
              <span className={isDark ? 'text-slate-300' : 'text-slate-600'}>F{i + 1}</span>
              <span className={`font-bold ${i === 0 ? 'text-sky-500' : (isDark ? 'text-white' : 'text-slate-800')}`}>{f}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
    <div className={`p-3 rounded-xl border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>Endometrium</span>
        <span className={`font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>{scenario.ivfData?.monitoring?.endometrium}</span>
      </div>
      <div className="flex items-center justify-between text-xs">
        <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>Estradiol (E2)</span>
        <span className={`font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>{scenario.ivfData?.monitoring?.e2Level}</span>
      </div>
    </div>
    <div className={`p-3 rounded-lg ${isDark ? 'bg-emerald-900/20 border border-emerald-800' : 'bg-emerald-50 border border-emerald-200'}`}>
      <p className={`text-xs ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>
        <strong>Trigger Agent:</strong> Lead follicle ≥18mm, E2 appropriate. Trigger Ready — schedule hCG tonight.
      </p>
    </div>
  </div>
);

const IVFRetrievalScreen = ({ scenario, isDark }) => (
  <div className="p-5 space-y-4" data-testid="ivf-retrieval-screen">
    <div className={`p-4 rounded-xl ${isDark ? 'bg-slate-800' : 'bg-rose-50'}`}>
      <div className="flex items-center gap-2 mb-2">
        <Target className={`w-5 h-5 ${isDark ? 'text-rose-400' : 'text-rose-600'}`} />
        <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>Trigger & Egg Retrieval (OPU)</span>
      </div>
    </div>
    <div className={`p-4 rounded-xl border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
      <p className={`text-xs font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>Trigger Details</p>
      <div className="space-y-1">
        {[
          { label: 'Trigger Type', value: 'hCG 10,000 IU' },
          { label: 'Trigger Time', value: '10:00 PM (36h before OPU)' },
          { label: 'OPU Scheduled', value: '10:00 AM (+36h)' },
        ].map((t, i) => (
          <div key={i} className="flex justify-between text-xs py-1">
            <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>{t.label}</span>
            <span className={`font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>{t.value}</span>
          </div>
        ))}
      </div>
    </div>
    <div className={`p-4 rounded-xl bg-gradient-to-br ${isDark ? 'from-rose-900/40 to-slate-800' : 'from-rose-50 to-white'} border ${isDark ? 'border-rose-800' : 'border-rose-200'}`}>
      <p className={`text-xs font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>OPU Outcome</p>
      <div className="flex items-center gap-6">
        <div className="text-center">
          <p className={`text-2xl font-bold ${isDark ? 'text-rose-400' : 'text-rose-600'}`}>{scenario.ivfData?.embryology?.oocytesRetrieved || 12}</p>
          <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Oocytes Retrieved</p>
        </div>
        <div className="text-center">
          <p className={`text-2xl font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>{scenario.ivfData?.embryology?.mii || 10}</p>
          <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Mature (MII)</p>
        </div>
      </div>
    </div>
    <div className={`p-3 rounded-lg ${isDark ? 'bg-emerald-900/20 border border-emerald-800' : 'bg-emerald-50 border border-emerald-200'}`}>
      <p className={`text-xs ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>
        <strong>OPU Agent:</strong> Procedure complete. No complications. 12 oocytes aspirated, 10 mature. Proceeding to ICSI.
      </p>
    </div>
  </div>
);

const IVFEmbryologyScreen = ({ scenario, isDark }) => (
  <div className="p-5 space-y-4" data-testid="ivf-embryology-screen">
    <div className={`p-4 rounded-xl ${isDark ? 'bg-slate-800' : 'bg-rose-50'}`}>
      <div className="flex items-center gap-2 mb-2">
        <Layers className={`w-5 h-5 ${isDark ? 'text-rose-400' : 'text-rose-600'}`} />
        <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>Embryology Lab (ICSI)</span>
      </div>
      <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Embryology Agent tracks oocytes through fertilization, culture, grading, and cryopreservation</p>
    </div>
    <div className="grid grid-cols-3 gap-2">
      {[
        { label: 'Fertilized (2PN)', val: scenario.ivfData?.embryology?.fertilized || 8 },
        { label: 'Day 3 Embryos', val: scenario.ivfData?.embryology?.day3Embryos || 7 },
        { label: 'Day 5 Blastocysts', val: scenario.ivfData?.embryology?.day5Blastocysts || 4 },
      ].map((m, i) => (
        <div key={i} className={`p-3 rounded-xl text-center ${isDark ? 'bg-slate-800' : 'bg-white border border-slate-200'}`}>
          <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>{m.val}</p>
          <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{m.label}</p>
        </div>
      ))}
    </div>
    <div className={`p-4 rounded-xl border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
      <p className={`text-xs font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>Embryo Grading</p>
      <div className="space-y-2">
        {[
          { id: 'E1', grade: 'Grade A (4AA)', status: 'Selected for Transfer', color: 'emerald' },
          { id: 'E2', grade: 'Grade A (4AB)', status: 'Selected for Transfer', color: 'emerald' },
          { id: 'E3', grade: 'Grade B (3BB)', status: 'Frozen', color: 'sky' },
          { id: 'E4', grade: 'Grade B (3BC)', status: 'Frozen', color: 'sky' },
        ].map((e, i) => (
          <div key={i} className={`flex items-center justify-between p-2 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-slate-50'}`}>
            <div className="flex items-center gap-2">
              <span className={`text-xs font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>{e.id}</span>
              <span className={`text-xs ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{e.grade}</span>
            </div>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
              e.color === 'emerald' ? (isDark ? 'bg-emerald-900/40 text-emerald-400' : 'bg-emerald-100 text-emerald-700') : (isDark ? 'bg-sky-900/40 text-sky-400' : 'bg-sky-100 text-sky-700')
            }`}>{e.status}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const IVFTransferScreen = ({ scenario, isDark }) => (
  <div className="p-5 space-y-4" data-testid="ivf-transfer-screen">
    <div className={`p-4 rounded-xl ${isDark ? 'bg-slate-800' : 'bg-rose-50'}`}>
      <div className="flex items-center gap-2 mb-2">
        <Heart className={`w-5 h-5 ${isDark ? 'text-rose-400' : 'text-rose-600'}`} />
        <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>Embryo Transfer</span>
      </div>
    </div>
    <div className={`p-4 rounded-xl border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
      <p className={`text-xs font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>Transfer Details</p>
      {[
        { label: 'Transfer Date', value: 'Day 5 (Blastocyst)' },
        { label: 'Embryos Transferred', value: '2 (Grade 4AA + 4AB)' },
        { label: 'Transfer Difficulty', value: 'Easy — smooth catheter passage' },
        { label: 'Endometrium', value: '10.2mm Triple-line pattern' },
        { label: 'Embryos Frozen', value: '2 remaining (Grade B)' },
      ].map((d, i) => (
        <div key={i} className="flex justify-between text-xs py-1.5 border-b last:border-0 border-slate-100 dark:border-slate-700">
          <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>{d.label}</span>
          <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>{d.value}</span>
        </div>
      ))}
    </div>
    <div className={`p-3 rounded-xl border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
      <p className={`text-xs font-bold mb-1 ${isDark ? 'text-white' : 'text-slate-800'}`}>Luteal Support</p>
      <div className="flex flex-wrap gap-1">
        {['Progesterone 400mg BD', 'Estradiol 2mg BD', 'Aspirin 75mg OD', 'Folic Acid 5mg'].map((m, i) => (
          <span key={i} className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>{m}</span>
        ))}
      </div>
    </div>
    <div className={`p-3 rounded-lg ${isDark ? 'bg-emerald-900/20 border border-emerald-800' : 'bg-emerald-50 border border-emerald-200'}`}>
      <p className={`text-xs ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>
        <strong>Transfer Agent:</strong> Successful transfer of 2 blastocysts. Discharge summary and medication calendar auto-generated.
      </p>
    </div>
  </div>
);

const IVFPregnancyScreen = ({ scenario, isDark }) => (
  <div className="p-5 space-y-4" data-testid="ivf-pregnancy-screen">
    <div className={`p-4 rounded-xl ${isDark ? 'bg-slate-800' : 'bg-rose-50'}`}>
      <div className="flex items-center gap-2 mb-2">
        <Activity className={`w-5 h-5 ${isDark ? 'text-rose-400' : 'text-rose-600'}`} />
        <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>Pregnancy Monitoring</span>
      </div>
      <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Pregnancy Agent tracks Beta hCG, milestones, and auto-converts to antenatal care</p>
    </div>
    <div className={`p-4 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 text-white`}>
      <p className="text-emerald-200 text-xs font-medium mb-1">Pregnancy Test Result</p>
      <p className="text-2xl font-bold">Beta hCG: {scenario.ivfData?.outcome?.betaHCG}</p>
      <p className="text-emerald-200 text-sm mt-1">Status: {scenario.ivfData?.outcome?.status}</p>
    </div>
    <div className={`p-4 rounded-xl border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
      <p className={`text-xs font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>Pregnancy Milestones</p>
      <div className="space-y-2">
        {[
          { week: 'Day 14', event: 'Beta hCG: 286 mIU/mL — Positive', done: true },
          { week: 'Day 16', event: 'Beta hCG doubling confirmed (48h rise)', done: true },
          { week: 'Week 6', event: 'Heartbeat confirmed on ultrasound', done: true },
          { week: 'Week 8', event: 'Viability scan — normal growth', done: false },
          { week: 'Week 12', event: 'NT scan + transition to routine antenatal', done: false },
        ].map((m, i) => (
          <div key={i} className={`flex items-center gap-3 p-2 rounded-lg ${m.done ? (isDark ? 'bg-emerald-900/20' : 'bg-emerald-50') : (isDark ? 'bg-slate-700' : 'bg-slate-50')}`}>
            <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${m.done ? 'bg-emerald-500' : (isDark ? 'bg-slate-600' : 'bg-slate-300')}`}>
              {m.done && <Check className="w-3 h-3 text-white" />}
            </div>
            <div>
              <p className={`text-xs font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>{m.week}</p>
              <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{m.event}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const IVFAnalyticsScreen = ({ scenario, isDark }) => (
  <div className="p-5 space-y-4" data-testid="ivf-analytics-screen">
    <div className={`p-4 rounded-xl ${isDark ? 'bg-slate-800' : 'bg-rose-50'}`}>
      <div className="flex items-center gap-2 mb-2">
        <BarChart3 className={`w-5 h-5 ${isDark ? 'text-rose-400' : 'text-rose-600'}`} />
        <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>IVF Analytics Dashboard</span>
      </div>
    </div>
    <div className="grid grid-cols-2 gap-3">
      {[
        { label: 'Cycles Managed', value: scenario.analytics?.cyclesManaged || 186 },
        { label: 'Avg Oocytes / Cycle', value: scenario.analytics?.avgOocytes || 11.4 },
        { label: 'Fertilization Rate', value: `${scenario.analytics?.fertRate || 76}%` },
        { label: 'Clinical Pregnancy Rate', value: `${scenario.analytics?.clinicalPregRate || 52}%` },
      ].map((m, i) => (
        <div key={i} className={`p-3 rounded-xl text-center ${isDark ? 'bg-slate-800' : 'bg-white border border-slate-200'}`}>
          <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{m.label}</p>
          <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>{m.value}</p>
        </div>
      ))}
    </div>
    <div className={`p-4 rounded-xl border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
      <p className={`text-xs font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>AI Agents Active in This Cycle</p>
      <div className="flex flex-wrap gap-1">
        {['Scribe', 'Couple Assessment', 'Investigation', 'Medication', 'Injection', 'Hormone', 'Follicular Monitor', 'Trigger', 'OPU', 'Embryology', 'Transfer', 'Pregnancy', 'Follow-up', 'Risk Engine', 'Copilot'].map((a, i) => (
          <span key={i} className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-rose-900/30 text-rose-400' : 'bg-rose-50 text-rose-700'}`}>{a}</span>
        ))}
      </div>
    </div>
    <div className={`p-3 rounded-lg ${isDark ? 'bg-sky-900/20 border border-sky-800' : 'bg-sky-50 border border-sky-200'}`}>
      <p className={`text-xs ${isDark ? 'text-sky-400' : 'text-sky-700'}`}>
        <strong>Skydoc captures 100% documentation:</strong> Consultation transcription, US dictation, procedure dictation, handover notes, counselling sessions, ICD codes, discharge summaries — all auto-generated from templates and prompts.
      </p>
    </div>
  </div>
);

export default InteractiveLiveSimulation;
